import { LitElement, css, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { SpinWheelApiClient, SpinWheelApiError } from './api';
import { fallbackCampaignConfig } from './defaults';
import { TriggerController } from './triggers';
import type {
  AnnouncementBarConfig,
  CampaignConfig,
  CustomCursorConfig,
  CurrencyCode,
  CurrencyConverterConfig,
  ProductCountdownItemConfig,
  ShoppableVideoCarouselConfig,
  ShoppablePlacementConfig,
  ShoppableVideoItemConfig,
  SpinResult,
  WidgetRuntimeOptions,
} from './types';
import { normalizeVietnamPhone, validateEmail, validateVietnamPhone } from './validation';
import { WheelRenderer } from './wheel-renderer';

const elementName = 'haravan-spin-wheel';
const defaultApiBaseUrl = 'https://aajcaughootiepspojey.supabase.co/functions/v1';
const customCursorStyleId = 'dada-plugins-custom-cursor';
const currencyOriginalValueAttr = 'data-dada-original-vnd';
const currencyStorageKeyPrefix = 'dada-plugins-currency';
const supportedCurrencyCodes = ['USD', 'EUR', 'JPY', 'INR', 'CNY', 'IDR', 'THB', 'KRW', 'MYR'];
const originalCurrencyText = new WeakMap<Text, string>();

@customElement(elementName)
export class HaravanSpinWheelElement extends LitElement {
  @property({ type: Boolean, attribute: 'preview' }) preview = false;
  @property({ attribute: false }) previewConfig?: Partial<CampaignConfig>;

  @query('canvas') private canvas?: HTMLCanvasElement;

  @state() private config: CampaignConfig = fallbackCampaignConfig;
  @state() private open = false;
  @state() private launcherReady = false;
  @state() private loading = true;
  @state() private submitting = false;
  @state() private spinning = false;
  @state() private error = '';
  @state() private email = '';
  @state() private phone = '';
  @state() private consentAccepted = false;
  @state() private result?: SpinResult;
  @state() private copied = false;
  @state() private rotationDeg = 0;
  @state() private selectedCurrency: CurrencyCode = 'VND';
  @state() private activeVideo?: ShoppableVideoItemConfig;
  @state() private activeVideoCarousel?: ShoppableVideoCarouselConfig;
  @state() private shoppableExpanded = false;
  @state() private shoppableMuted = true;
  @state() private cartNotice = '';
  @state() private cartItemCount?: number;
  @state() private activeAnnouncementIndex = 0;

  private api?: SpinWheelApiClient;
  private renderer?: WheelRenderer;
  private trigger = new TriggerController();
  private leadId = '';
  private fingerprint = '';
  private countdownTimer?: number;
  private announcementRotationTimer?: number;
  private announcementViewTracked = false;
  private dismissedAnnouncementIds = new Set<string>();
  @state() private announcementDismissed = false;
  private currencyObserver?: MutationObserver;
  private currencyScanTimer?: number;
  private announcementOriginalBodyPaddingTop?: string;
  private announcementOffsetApplied = false;
  private shoppableTouchStartY?: number;
  private shoppableLastNavigateAt = 0;
  private announcementMountFrame?: number;
  private shoppablePlacementFrame?: number;
  private productCountdownTimer?: number;
  private productCountdownBadge?: HTMLElement;
  private externalScriptErrorGuard?: (event: Event) => void;

  static styles = css`
    :host {
      all: initial;
      display: block;
      color: var(--sw-text, #172026);
      font-family:
        Inter,
        ui-sans-serif,
        system-ui,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        sans-serif;
    }

    :host([data-custom-cursor]),
    :host([data-custom-cursor]) * {
      cursor: var(--dada-custom-cursor) !important;
    }

    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    .launcher {
      position: fixed;
      left: 0;
      top: 56%;
      transform: translateY(-50%);
      z-index: 2147483000;
      border: 0;
      width: 94px;
      height: 188px;
      border-radius: 0 96px 96px 0;
      background: var(--sw-launcher-gradient);
      color: #ffffff;
      box-shadow: 0 18px 42px rgba(15, 23, 42, 0.28);
      cursor: pointer;
      font:
        900 12px/1.05 Inter,
        ui-sans-serif,
        system-ui,
        sans-serif;
      padding: 0;
      overflow: hidden;
      transition:
        transform 180ms ease,
        box-shadow 180ms ease,
        filter 180ms ease;
    }

    .launcher::before {
      content: '';
      position: absolute;
      inset: 12px 12px 12px 0;
      border-radius: 0 88px 88px 0;
      border: 2px solid var(--sw-launcher-divider);
      pointer-events: none;
    }

    .launcher::after {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      width: 18px;
      height: 36px;
      border-radius: 0 999px 999px 0;
      background: var(--sw-launcher-divider);
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.42);
      transform: translateY(-50%);
      pointer-events: none;
    }

    .launcher:hover {
      transform: translateY(-50%) translateX(4px);
      box-shadow: 0 22px 54px rgba(15, 23, 42, 0.34);
      filter: saturate(1.08);
    }

    .launcherText {
      position: absolute;
      right: 11px;
      top: 50%;
      display: grid;
      place-items: center;
      width: 42px;
      min-height: 92px;
      color: var(--sw-launcher-text);
      text-align: center;
      text-shadow: 0 1px 3px rgba(15, 23, 42, 0.38);
      text-transform: uppercase;
      transform: translateY(-50%);
      word-break: normal;
    }

    .launcherPointer {
      position: absolute;
      right: 3px;
      top: 50%;
      width: 0;
      height: 0;
      border-top: 9px solid transparent;
      border-bottom: 9px solid transparent;
      border-left: 14px solid var(--sw-launcher-pointer);
      filter: drop-shadow(0 1px 2px rgba(15, 23, 42, 0.32));
      transform: translateY(-50%);
      pointer-events: none;
    }

    .overlay {
      position: fixed;
      inset: 0;
      z-index: 2147483001;
      display: grid;
      place-items: center;
      padding: 18px;
      background: var(--sw-overlay, rgba(15, 23, 42, 0.58));
    }

    .modal {
      width: min(940px, 100%);
      max-height: min(92vh, 720px);
      overflow: auto;
      display: grid;
      align-items: center;
      grid-template-columns: minmax(280px, 1fr) minmax(280px, 0.88fr);
      gap: 28px;
      background:
        linear-gradient(135deg, rgba(15, 118, 110, 0.08), transparent 34%),
        linear-gradient(315deg, rgba(245, 158, 11, 0.13), transparent 30%), var(--sw-surface, #fff);
      color: var(--sw-text, #172026);
      border: 1px solid var(--sw-border, #d8e1e8);
      border-radius: 8px;
      box-shadow: 0 24px 80px rgba(15, 23, 42, 0.32);
      padding: 34px 28px 28px;
      position: relative;
    }

    .wheelWrap {
      display: grid;
      place-items: center;
      min-width: 0;
      min-height: 430px;
    }

    canvas {
      width: min(100%, 430px);
      aspect-ratio: 1;
      display: block;
    }

    .panel {
      display: grid;
      align-content: center;
      gap: 16px;
      min-width: 0;
    }

    .header {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      align-items: start;
    }

    h2 {
      margin: 0;
      font-size: 28px;
      line-height: 1.08;
      letter-spacing: 0;
    }

    p {
      margin: 0;
      color: var(--sw-muted, #64748b);
      font-size: 15px;
      line-height: 1.45;
    }

    .close {
      position: absolute;
      top: 14px;
      right: 14px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      padding: 0;
      border: 0;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.9);
      color: color-mix(in srgb, var(--sw-text, #172026) 74%, transparent);
      cursor: pointer;
      font-size: 0;
      line-height: 1;
      box-shadow:
        inset 0 0 0 1px rgba(15, 23, 42, 0.08),
        0 10px 28px rgba(15, 23, 42, 0.08);
      -webkit-backdrop-filter: blur(10px);
      backdrop-filter: blur(10px);
      transition:
        background 160ms ease,
        box-shadow 160ms ease,
        color 160ms ease,
        transform 160ms ease;
    }

    .closeIcon {
      position: relative;
      display: inline-grid;
      place-items: center;
      width: 18px;
      height: 18px;
      border-radius: 999px;
      background: transparent;
      color: inherit;
      transition: color 160ms ease;
    }

    .closeIcon::before,
    .closeIcon::after {
      content: '';
      position: absolute;
      width: 9px;
      height: 1.4px;
      border-radius: 999px;
      background: currentColor;
    }

    .closeIcon::before {
      transform: rotate(45deg);
    }

    .closeIcon::after {
      transform: rotate(-45deg);
    }

    .close:hover {
      background: #ffffff;
      color: var(--sw-text, #172026);
      box-shadow:
        inset 0 0 0 1px rgba(15, 23, 42, 0.12),
        0 14px 34px rgba(15, 23, 42, 0.12);
      transform: scale(1.04);
    }

    .close:hover .closeIcon {
      color: var(--sw-primary, #0f766e);
    }

    .close:focus-visible {
      outline: 3px solid color-mix(in srgb, var(--sw-primary, #0f766e) 28%, transparent);
      outline-offset: 3px;
    }

    @media (max-width: 560px) {
      .close {
        top: 10px;
        right: 10px;
      }

      .closeIcon {
        width: 18px;
        height: 18px;
      }
    }

    form {
      display: grid;
      gap: 10px;
    }

    input {
      width: 100%;
      min-height: 46px;
      border: 1px solid var(--sw-border, #d8e1e8);
      border-radius: 6px;
      color: var(--sw-text, #172026);
      background: #fff;
      font:
        500 15px/1.2 Inter,
        ui-sans-serif,
        system-ui,
        sans-serif;
      padding: 0 13px;
    }

    input:focus {
      border-color: var(--sw-primary, #0f766e);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--sw-primary, #0f766e), transparent 78%);
      outline: none;
    }

    .phoneField {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      align-items: stretch;
      min-height: 46px;
      border: 1px solid var(--sw-border, #d8e1e8);
      border-radius: 6px;
      background: #fff;
      overflow: hidden;
    }

    .phonePrefix {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      border-right: 1px solid var(--sw-border, #d8e1e8);
      background: #f8fafc;
      color: var(--sw-text, #172026);
      font:
        800 14px/1 Inter,
        ui-sans-serif,
        system-ui,
        sans-serif;
      padding: 0 12px;
      white-space: nowrap;
    }

    .phoneFlag {
      font-size: 16px;
      line-height: 1;
    }

    .phoneField input {
      min-height: 44px;
      border: 0;
      border-radius: 0;
    }

    .phoneField:focus-within {
      border-color: var(--sw-primary, #0f766e);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--sw-primary, #0f766e), transparent 78%);
    }

    .phoneField input:focus {
      box-shadow: none;
    }

    label.consent {
      display: flex;
      gap: 9px;
      align-items: flex-start;
      color: var(--sw-muted, #64748b);
      font-size: 13px;
      line-height: 1.35;
    }

    label.consent input {
      width: 16px;
      min-height: 16px;
      margin-top: 1px;
      padding: 0;
    }

    .submit {
      min-height: 48px;
      border: 0;
      border-radius: 6px;
      background: var(--sw-primary, #0f766e);
      color: var(--sw-button-text, #fff);
      cursor: pointer;
      font:
        800 15px/1 Inter,
        ui-sans-serif,
        system-ui,
        sans-serif;
    }

    .submit:disabled {
      cursor: not-allowed;
      opacity: 0.72;
    }

    .resultHint {
      color: var(--sw-muted, #64748b);
      font-size: 14px;
      line-height: 1.45;
    }

    .error {
      color: #b42318;
      font-size: 13px;
      line-height: 1.35;
    }

    .coupon {
      display: inline-grid;
      place-items: center;
      width: min(260px, 100%);
      min-height: 76px;
      max-width: 100%;
      border: 2px dashed var(--sw-primary, #0f766e);
      border-radius: 8px;
      background:
        linear-gradient(135deg, rgba(15, 118, 110, 0.12), rgba(245, 158, 11, 0.14)), #f0fdfa;
      color: #115e59;
      cursor: pointer;
      font:
        950 30px/1 Inter,
        ui-sans-serif,
        system-ui,
        sans-serif;
      letter-spacing: 0;
      padding: 14px 18px;
      word-break: break-word;
      transition:
        transform 160ms ease,
        box-shadow 160ms ease,
        background 160ms ease;
    }

    .coupon:hover {
      transform: translateY(-1px);
      box-shadow: 0 14px 28px rgba(15, 118, 110, 0.18);
    }

    .copyStatus {
      color: var(--sw-muted, #64748b);
      font-size: 13px;
      line-height: 1.35;
    }

    .poweredBy {
      justify-self: start;
      color: color-mix(in srgb, var(--sw-muted, #64748b), transparent 12%);
      font:
        600 11px/1.2 Inter,
        ui-sans-serif,
        system-ui,
        sans-serif;
      text-decoration: none;
    }

    .poweredBy:hover {
      color: var(--sw-primary, #0f766e);
      text-decoration: underline;
      text-underline-offset: 3px;
    }

    .announcementBar {
      position: fixed;
      z-index: 2147482999;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      width: 100%;
      min-height: 50px;
      box-sizing: border-box;
      border: 0;
      border-radius: 0;
      background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.18), transparent 34%),
        radial-gradient(circle at 18% 50%, rgba(255, 255, 255, 0.16), transparent 18%),
        var(--ab-bg, #111827);
      color: var(--ab-text, #ffffff);
      box-shadow: 0 12px 32px rgba(15, 23, 42, 0.2);
      padding: 7px 34px 7px 16px;
      text-align: center;
      text-decoration: none;
      font-family: Inter, ui-sans-serif, system-ui, sans-serif;
      overflow: hidden;
    }

    .announcementBar.clickable {
      cursor: pointer;
    }

    .announcementBar.scrollable {
      position: relative;
      inset: auto;
    }

    .announcementBar.stacked.top {
      top: calc(var(--ab-stack-index, 0) * 50px);
    }

    .announcementBar.stacked.bottom {
      bottom: calc(var(--ab-stack-index, 0) * 50px);
    }

    .announcementBar.alternate.effect-fade {
      animation: announcementFadeIn 220ms ease both;
    }

    .announcementBar.alternate.effect-slide {
      animation: announcementBarSlideIn 260ms ease both;
    }

    .announcementBar.effect-none {
      background: var(--ab-bg, #111827);
    }

    .announcementBar.effect-spotlight {
      background:
        radial-gradient(circle at 12% 50%, rgba(255, 255, 255, 0.3), transparent 18%),
        linear-gradient(90deg, rgba(255, 255, 255, 0.2), transparent 34%, rgba(255, 255, 255, 0.12)),
        var(--ab-bg, #111827);
    }

    .announcementBar.left {
      justify-content: flex-start;
      text-align: left;
    }

    .announcementBar.right {
      justify-content: flex-end;
      text-align: right;
    }

    .announcementBar.sliding .announcementCopy {
      animation: announcementSlide 18s linear infinite;
      animation-delay: 0s;
      min-width: max-content;
    }

    .announcementIcon {
      width: 20px;
      min-width: 20px;
      height: 20px;
      border-radius: 6px;
      object-fit: contain;
    }

    .announcementBar.top,
    .announcementBar.bottom {
      left: 0;
      right: 0;
    }

    .announcementBar.top {
      top: 0;
    }

    .announcementBar.bottom {
      bottom: 0;
    }

    .announcementCopy {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }

    .announcementBar strong {
      flex: 0 0 auto;
      font-size: 15px;
      line-height: 1.18;
    }

    .announcementBar span {
      min-width: 0;
      opacity: 0.84;
      overflow: hidden;
      font-size: 13px;
      line-height: 1.3;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .announcementBar time {
      flex: 0 0 auto;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.16);
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
      font-size: 12px;
      font-weight: 850;
      line-height: 1;
      padding: 7px 10px;
    }

    .announcementCta {
      flex: 0 0 auto;
      border: var(--ab-cta-border-size, 0) solid var(--ab-cta-border-color, #ffffff);
      border-radius: var(--ab-cta-radius, 999px);
      background: var(--ab-cta-bg, #ffffff);
      color: var(--ab-cta-text, #111827);
      font-size: 12px;
      font-weight: 800;
      line-height: 1;
      opacity: 1;
      padding: 8px 12px;
      white-space: nowrap;
    }

    .announcementDismiss {
      position: absolute;
      top: 6px;
      right: 8px;
      width: 18px;
      height: 18px;
      border: 0;
      background: transparent;
      color: var(--ab-dismiss, currentColor);
      cursor: pointer;
      font-size: 0;
      line-height: 1;
      opacity: 0.76;
      padding: 0;
      transition:
        opacity 160ms ease,
        transform 160ms ease;
    }

    .announcementDismiss::before,
    .announcementDismiss::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 10px;
      height: 1.5px;
      border-radius: 999px;
      background: currentColor;
    }

    .announcementDismiss::before {
      transform: translate(-50%, -50%) rotate(45deg);
    }

    .announcementDismiss::after {
      transform: translate(-50%, -50%) rotate(-45deg);
    }

    .announcementDismiss:hover {
      opacity: 1;
      transform: scale(1.12);
    }

    @keyframes announcementSlide {
      from {
        transform: translateX(0);
      }

      to {
        transform: translateX(-100%);
      }
    }

    @keyframes announcementFadeIn {
      from {
        opacity: 0;
      }

      to {
        opacity: 1;
      }
    }

    @keyframes announcementBarSlideIn {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }

      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .currencySwitcher {
      position: fixed;
      z-index: 2147482999;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-height: 42px;
      border: 1px solid rgba(15, 23, 42, 0.1);
      border-radius: 999px;
      background:
        linear-gradient(135deg, color-mix(in srgb, var(--cc-primary) 16%, transparent), transparent 42%),
        rgba(255, 255, 255, 0.96);
      color: #172026;
      box-shadow: 0 14px 34px rgba(15, 23, 42, 0.16);
      padding: 0 12px 0 14px;
      font:
        800 12px/1 Inter,
        ui-sans-serif,
        system-ui,
        sans-serif;
      backdrop-filter: blur(16px);
    }

    .currencySwitcher.top-left {
      left: 16px;
      top: 16px;
    }

    .currencySwitcher.top-right {
      right: 16px;
      top: 16px;
    }

    .currencySwitcher.bottom-left {
      left: 16px;
      bottom: 16px;
    }

    .currencySwitcher.bottom-right {
      right: 16px;
      bottom: 16px;
    }

    .currencySwitcherIcon {
      display: grid;
      place-items: center;
      width: 22px;
      height: 22px;
      border-radius: 999px;
      background: var(--cc-primary);
      color: #ffffff;
      font-size: 12px;
      font-weight: 900;
    }

    .currencySwitcher select {
      appearance: none;
      -webkit-appearance: none;
      min-height: 34px;
      border: 0;
      border-radius: 999px;
      background:
        linear-gradient(45deg, transparent 50%, #667085 50%) calc(100% - 12px) 14px / 5px 5px
          no-repeat,
        linear-gradient(135deg, #667085 50%, transparent 50%) calc(100% - 8px) 14px / 5px 5px
          no-repeat,
        transparent;
      color: #172026;
      cursor: pointer;
      font: inherit;
      outline: none;
      padding: 0 22px 0 0;
      -webkit-tap-highlight-color: transparent;
    }

    .currencySwitcher select:focus,
    .currencySwitcher select:focus-visible,
    .currencySwitcher select:active {
      outline: none;
      box-shadow: none;
    }

    .shoppableSection {
      display: block;
      width: 100%;
      margin: 42px auto;
      padding: 0 18px;
      color: inherit;
      font-family: inherit;
    }

    .shoppableInner {
      width: min(1180px, 100%);
      margin: 0 auto;
    }

    .shoppableTitle {
      margin: 0 0 18px;
      color: inherit;
      font: inherit;
      font-size: clamp(24px, 3vw, 40px);
      font-weight: 700;
      line-height: 1.15;
      text-align: inherit;
      letter-spacing: 0;
    }

    .shoppableRail {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: minmax(210px, 280px);
      gap: 18px;
      overflow-x: auto;
      overscroll-behavior-x: contain;
      padding: 4px 2px 12px;
      scroll-snap-type: x mandatory;
    }

    .shoppableCard {
      position: relative;
      display: block;
      min-height: 430px;
      aspect-ratio: 9 / 16;
      border: var(--sv-card-border-size, 0) solid var(--sv-card-border-color, #111827);
      border-radius: var(--sv-card-radius, 20px);
      background: var(--sv-card-placeholder, #111827);
      box-shadow: 0 16px 34px rgba(15, 23, 42, 0.18);
      cursor: pointer;
      overflow: hidden;
      padding: 0;
      scroll-snap-align: start;
    }

    .shoppableCard video,
    .shoppableCard iframe,
    .shoppableCard .embedCrop,
    .shoppableCard .videoFallback {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      min-height: 430px;
      display: block;
      object-fit: cover;
      color: #fff;
    }

    .shoppableCard video,
    .shoppableCard iframe {
      background: var(--sv-card-placeholder, #111827);
      border: 0;
      transform-origin: center;
      opacity: 0;
      pointer-events: none;
      transition: opacity 180ms ease;
    }

    .shoppableCard .embedCrop {
      overflow: hidden;
      background: var(--sv-card-placeholder, #111827);
    }

    .shoppableCard .embedCrop iframe {
      left: 50%;
      right: auto;
      width: 178%;
      height: 100%;
      max-width: none;
      transform: translateX(-50%);
    }

    .shoppableCard video.loaded,
    .shoppableCard iframe.loaded {
      opacity: 1;
    }

    .shoppableCard::after {
      content: '';
      position: absolute;
      inset: auto 0 0;
      height: 42%;
      background: linear-gradient(transparent, rgba(0, 0, 0, 0.76));
      pointer-events: none;
    }

    .shoppableCardInfo {
      position: absolute;
      left: 12px;
      right: 12px;
      bottom: 12px;
      z-index: 1;
      display: grid;
      grid-template-columns: 46px 1fr;
      gap: 10px;
      align-items: end;
      color: #ffffff;
      text-align: left;
    }

    .shoppableCardInfo.textOnly {
      grid-template-columns: minmax(0, 1fr);
      align-items: end;
    }

    .shoppableCardInfo img {
      width: 46px;
      height: 46px;
      border-radius: 10px;
      object-fit: cover;
      background: #fff;
    }

    .shoppableCardInfo strong {
      display: -webkit-box;
      margin: 0 0 4px;
      overflow: hidden;
      font-size: 14px;
      line-height: 1.2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .shoppableCardInfo span {
      font-size: 13px;
      font-weight: 800;
    }

    .videoTitleOnly {
      position: absolute;
      left: 18px;
      right: 82px;
      bottom: 26px;
      z-index: 2;
      display: block;
      color: #ffffff;
      font-size: 22px;
      font-weight: 760;
      line-height: 1.14;
      text-shadow: 0 2px 22px rgba(0, 0, 0, 0.66);
    }

    .videoOverlay {
      position: fixed;
      inset: 0;
      z-index: 2147483002;
      display: grid;
      place-items: center;
      background: rgba(0, 0, 0, 0.88);
      overscroll-behavior: contain;
      touch-action: none;
    }

    .videoStage {
      position: relative;
      width: min(430px, 100vw);
      height: min(760px, 100vh);
      overflow: hidden;
      background: #000;
      overscroll-behavior: contain;
    }

    .videoStage video,
    .videoStage iframe,
    .videoStage .videoPoster,
    .videoStage .videoFallback {
      width: 100%;
      height: 100%;
      border: 0;
      object-fit: cover;
    }

    .videoStage .videoFallback {
      display: grid;
      place-items: center;
      padding: 28px;
      color: #ffffff;
      text-align: center;
    }

    .videoClose,
    .videoMute,
    .videoCart {
      position: absolute;
      z-index: 2;
      display: grid;
      place-items: center;
      width: 44px;
      height: 44px;
      border: 0;
      border-radius: 999px;
      background: rgba(15, 15, 15, 0.72);
      color: #ffffff;
      cursor: pointer;
      font-size: 24px;
      backdrop-filter: blur(14px);
      box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.18),
        0 14px 38px rgba(0, 0, 0, 0.22);
      transition:
        background 160ms ease,
        box-shadow 160ms ease,
        transform 160ms ease;
    }

    .videoClose:hover,
    .videoMute:hover,
    .videoCart:hover {
      background: rgba(15, 15, 15, 0.86);
      box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.24),
        0 18px 44px rgba(0, 0, 0, 0.28);
      transform: translateY(-1px);
    }

    .videoClose {
      top: 18px;
      right: 18px;
      font-size: 0;
    }

    .videoClose::before,
    .videoClose::after {
      content: '';
      position: absolute;
      width: 17px;
      height: 2px;
      border-radius: 999px;
      background: currentColor;
    }

    .videoClose::before {
      transform: rotate(45deg);
    }

    .videoClose::after {
      transform: rotate(-45deg);
    }

    .videoMute {
      right: 18px;
      bottom: 168px;
      font-size: 18px;
    }

    .videoCart {
      right: 18px;
      bottom: 112px;
      font-size: 18px;
    }

    .videoCart svg {
      width: 22px;
      height: 22px;
      stroke: currentColor;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .videoMute svg {
      width: 23px;
      height: 23px;
      stroke: currentColor;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .videoCartBadge {
      position: absolute;
      top: -5px;
      right: -5px;
      display: grid;
      place-items: center;
      min-width: 19px;
      height: 19px;
      border-radius: 999px;
      background: #ef4444;
      color: #ffffff;
      font-size: 11px;
      font-weight: 900;
      line-height: 1;
      padding: 0 5px;
    }

    .productBanner {
      position: absolute;
      left: 12px;
      right: 12px;
      bottom: 18px;
      z-index: 2;
      display: grid;
      grid-template-columns: 62px 1fr auto;
      gap: 12px;
      align-items: center;
      border-radius: 16px;
      background: var(--sv-product-bg, #ffffff);
      background: color-mix(in srgb, var(--sv-product-bg, #ffffff) 92%, transparent);
      color: var(--sv-product-primary, #000000);
      padding: 10px;
      text-align: left;
      backdrop-filter: blur(14px);
    }

    .productBannerClose {
      position: absolute;
      top: 8px;
      right: 8px;
      display: grid;
      place-items: center;
      width: 24px;
      height: 24px;
      border: 0;
      border-radius: 999px;
      background: rgba(15, 23, 42, 0.08);
      background: color-mix(in srgb, var(--sv-product-secondary, #4b5563) 14%, transparent);
      color: var(--sv-product-primary, #000000);
      cursor: pointer;
      font-size: 0;
      line-height: 1;
      padding: 0;
      transition:
        background 160ms ease,
        transform 160ms ease;
    }

    .productBannerClose::before,
    .productBannerClose::after {
      content: '';
      position: absolute;
      width: 10px;
      height: 1.6px;
      border-radius: 999px;
      background: currentColor;
    }

    .productBannerClose::before {
      transform: rotate(45deg);
    }

    .productBannerClose::after {
      transform: rotate(-45deg);
    }

    .productBannerClose:hover {
      background: color-mix(in srgb, var(--sv-product-secondary, #4b5563) 22%, transparent);
      transform: scale(1.06);
    }

    .productBanner.expanded {
      grid-template-columns: 96px 1fr;
      align-items: start;
    }

    .productBanner img {
      width: 62px;
      height: 62px;
      border-radius: 10px;
      object-fit: cover;
      background: #fff;
    }

    .productBanner.expanded img {
      width: 96px;
      height: 96px;
    }

    .productBanner h3 {
      margin: 0 0 6px;
      font-size: 18px;
      line-height: 1.18;
    }

    .productBanner p {
      margin: 0;
      color: var(--sv-product-secondary, #4b5563);
      font-size: 13px;
      line-height: 1.35;
    }

    .productPrice {
      display: flex;
      gap: 8px;
      align-items: baseline;
      font-weight: 900;
    }

    .productPrice s {
      color: var(--sv-product-secondary, #6b7280);
      font-weight: 650;
    }

    .shopNow,
    .productActions button,
    .productActions a {
      border: 0;
      border-radius: 10px;
      background: var(--sv-product-primary, #000000);
      color: var(--sv-product-bg, #ffffff);
      cursor: pointer;
      font: 800 13px/1 Inter, ui-sans-serif, system-ui, sans-serif;
      padding: 12px 14px;
      text-decoration: none;
      white-space: nowrap;
    }

    .productActions {
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-top: 12px;
    }

    .productActions a {
      display: grid;
      place-items: center;
      background: var(--sv-product-bg, #ffffff);
      color: var(--sv-product-primary, #000000);
      box-shadow: inset 0 0 0 1px var(--sv-product-primary, #000000);
    }

    @media (max-width: 720px) {
      .announcementBar {
        gap: 8px;
        justify-content: space-between;
        min-height: 50px;
        padding: 7px 12px;
        text-align: left;
      }

      .announcementCopy {
        display: grid;
        gap: 2px;
      }

      .announcementBar strong {
        font-size: 13px;
      }

      .announcementBar span {
        font-size: 12px;
      }

      .currencySwitcher {
        min-height: 40px;
      }

      .currencySwitcher.top-left,
      .currencySwitcher.bottom-left {
        left: 10px;
      }

      .currencySwitcher.top-right,
      .currencySwitcher.bottom-right {
        right: 10px;
      }

      .currencySwitcher.top-left,
      .currencySwitcher.top-right {
        top: 10px;
      }

      .currencySwitcher.bottom-left,
      .currencySwitcher.bottom-right {
        bottom: 10px;
      }

      .modal {
        grid-template-columns: 1fr;
        gap: 10px;
        padding: 50px 16px 16px;
      }

      .wheelWrap {
        min-height: 320px;
      }

      canvas {
        width: min(100%, 320px);
      }

      h2 {
        font-size: 23px;
      }

      .launcher {
        top: auto;
        bottom: 72px;
        width: 78px;
        height: 156px;
      }

      .launcherText {
        right: 8px;
        width: 36px;
        min-height: 78px;
        font-size: 11px;
      }
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.installExternalScriptErrorGuard();
    void this.boot();
  }

  disconnectedCallback(): void {
    this.removeExternalScriptErrorGuard();
    this.trigger.stop();
    this.renderer?.stop();
    this.stopCountdownTimer();
    this.stopAnnouncementRotationTimer();
    this.cancelPendingPlacementWork();
    this.stopCurrencyConverter();
    this.restoreAnnouncementPageOffset();
    this.clearProductCountdownBadge();
    this.clearShadowCustomCursor();
    removeCustomCursor();
    super.disconnectedCallback();
  }

  protected updated(changedProperties: PropertyValues<this>): void {
    if (this.preview && changedProperties.has('previewConfig') && this.previewConfig) {
      this.config = mergePreviewCampaignConfig(this.previewConfig);
      this.selectedCurrency = resolveInitialCurrency(
        this.config.plugins?.currencyConverter,
        this.config.shopDomain,
      );
      this.launcherReady = true;
      this.loading = false;
      this.result = undefined;
      this.rotationDeg = 0;
      this.runPluginSafely('announcement countdown', () =>
        this.syncCountdownTimer(this.getActiveAnnouncementBar()),
      );
      this.runPluginSafely('announcement rotation', () => this.syncAnnouncementRotationTimer());
      this.runPluginSafely('currency converter', () =>
        this.syncCurrencyConverter(this.config.plugins?.currencyConverter),
      );
    }

    if (!this.preview) void this.runPluginSafely('announcement analytics', () => this.trackAnnouncementViewOnce());
    this.runPluginSafely('announcement mount', () => this.scheduleAnnouncementMountPosition());
    this.runPluginSafely('announcement host state', () => this.syncAnnouncementHostState());
    this.runPluginSafely('announcement layout', () => this.syncAnnouncementPageOffset());
    this.runPluginSafely('shoppable placement', () => this.scheduleShoppablePlacement());
    this.runPluginSafely('product countdown', () => this.syncProductCountdown());

    if ((changedProperties as Map<PropertyKey, unknown>).has('activeVideo') && this.activeVideo) {
      void this.updateComplete.then(() => {
        const overlay = this.renderRoot.querySelector<HTMLElement>('.videoOverlay');
        overlay?.focus({ preventScroll: true });
        this.playActiveShoppableVideo();
      });
    }

    this.applyTheme();
    if (this.canvas && (!this.renderer || !this.renderer.isForCanvas(this.canvas))) {
      this.renderer?.stop();
      this.renderer = new WheelRenderer(this.canvas);
      window.requestAnimationFrame(() => this.drawWheel());
    }
    this.drawWheel();
  }

  render() {
    if (this.loading) {
      return nothing;
    }

    const pluginChrome = html`${this.renderPluginSafely('announcement bar', () =>
      this.renderAnnouncementBar(),
    )}
    ${this.renderPluginSafely('currency switcher', () => this.renderCurrencySwitcher())}
    ${this.renderPluginSafely('shoppable videos', () => this.renderShoppableVideos())}
    ${this.renderPluginSafely('shoppable overlay', () => this.renderShoppableVideoOverlay())}`;
    if (!this.config.active || !this.launcherReady) return pluginChrome;

    return html`
      ${pluginChrome}
      <button class="launcher" @click=${this.openModal}>
        <span class="launcherText">${this.config.copy.triggerLabel}</span>
        <span class="launcherPointer" aria-hidden="true"></span>
      </button>

      ${this.open
        ? html`
            <div class="overlay" @click=${this.onOverlayClick}>
              <section
                class="modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="spin-wheel-title"
              >
                <div class="wheelWrap">
                  <canvas aria-label="Prize wheel"></canvas>
                </div>
                <div class="panel">
                  <button
                    class="close"
                    type="button"
                    aria-label=${this.config.copy.closeLabel}
                    @click=${this.closeModal}
                  >
                    <span class="closeIcon" aria-hidden="true"></span>
                  </button>
                  <div class="header">
                    <div>
                      <h2 id="spin-wheel-title">
                        ${this.result ? this.config.copy.successTitle : this.config.copy.title}
                      </h2>
                      <p>${this.result ? this.result.segment.label : this.config.copy.subtitle}</p>
                    </div>
                  </div>

                  ${this.result ? this.renderResult() : this.renderForm()}
                  <a
                    class="poweredBy"
                    href="https://dadaplugins.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Powered by Dada Plugins
                  </a>
                </div>
              </section>
            </div>
          `
        : nothing}
    `;
  }

  private renderPluginSafely(pluginName: string, renderPlugin: () => unknown): unknown {
    try {
      return renderPlugin();
    } catch (error) {
      console.warn(`[Dada Plugins] ${pluginName} skipped.`, error);
      return nothing;
    }
  }

  private runPluginSafely(pluginName: string, runPlugin: () => void | Promise<void>): void {
    try {
      const result = runPlugin();
      if (result instanceof Promise) {
        result.catch((error) => console.warn(`[Dada Plugins] ${pluginName} skipped.`, error));
      }
    } catch (error) {
      console.warn(`[Dada Plugins] ${pluginName} skipped.`, error);
    }
  }

  private installExternalScriptErrorGuard(): void {
    if (this.externalScriptErrorGuard || typeof window === 'undefined') return;

    this.externalScriptErrorGuard = (event: Event) => {
      if (!isKnownHaravanExternalScriptError(event)) return;
      event.stopImmediatePropagation();
      event.stopPropagation();
      console.warn('[Dada Plugins] Ignored Haravan external script load failure.');
    };

    window.addEventListener('error', this.externalScriptErrorGuard, true);
    document.addEventListener('error', this.externalScriptErrorGuard, true);
  }

  private removeExternalScriptErrorGuard(): void {
    if (!this.externalScriptErrorGuard || typeof window === 'undefined') return;
    window.removeEventListener('error', this.externalScriptErrorGuard, true);
    document.removeEventListener('error', this.externalScriptErrorGuard, true);
    this.externalScriptErrorGuard = undefined;
  }

  private renderAnnouncementBar() {
    const announcements = this.getRenderableAnnouncementBars();
    if (!announcements.length) return nothing;
    const settings = this.config.plugins?.announcementBarSettings;
    if (!settings?.allowMultiple || settings.displayMode === 'alternate') {
      return this.renderAnnouncementBarItem(announcements[0], 0);
    }

    return html`${announcements.map((announcement, index) =>
      this.renderAnnouncementBarItem(announcement, index),
    )}`;
  }

  private renderAnnouncementBarItem(announcement: AnnouncementBarConfig, index: number) {
    if (!announcement?.active) return nothing;
    if (this.isAnnouncementDismissed(announcement)) return nothing;
    if (isAnnouncementSchedulePending(announcement)) return nothing;
    if (isAnnouncementCountdownExpired(announcement)) return nothing;

    const style = [
      `--ab-bg: ${announcement.backgroundColor}`,
      `--ab-text: ${announcement.textColor}`,
      `--ab-cta-bg: ${announcement.ctaBackgroundColor}`,
      `--ab-cta-text: ${announcement.ctaTextColor}`,
      `--ab-cta-radius: ${announcement.ctaCornerRadius}px`,
      `--ab-cta-border-size: ${announcement.ctaBorderSize}px`,
      `--ab-cta-border-color: ${announcement.ctaBorderColor}`,
      `--ab-dismiss: ${getReadableTextColor(announcement.backgroundColor)}`,
      `--ab-stack-index: ${index}`,
    ].join('; ');
    const settings = this.config.plugins?.announcementBarSettings;
    const multiClass = settings?.allowMultiple
      ? settings.displayMode === 'alternate'
        ? `alternate effect-${settings.transition}`
        : 'stacked'
      : '';
    const countdownVisible =
      announcement.showCountdown !== false && isAnnouncementCountdownStarted(announcement);
    const clickableClass = announcement.linkUrl ? 'clickable' : '';
    const content = html`
      <div class="announcementCopy">
        ${announcement.iconUrl
          ? html`<img class="announcementIcon" src=${announcement.iconUrl} alt="" />`
          : nothing}
        <strong>${announcement.title}</strong>
        ${announcement.subtitle ? html`<span>${announcement.subtitle}</span>` : nothing}
      </div>
      ${countdownVisible && announcement.countdownEndsAt
        ? html`<time>${formatAnnouncementCountdown(announcement.countdownEndsAt)}</time>`
        : nothing}
      ${announcement.ctaEnabled && announcement.ctaText
        ? html`<span class="announcementCta">${announcement.ctaText}</span>`
        : nothing}
      ${announcement.dismissEnabled
        ? html`<button
            class="announcementDismiss"
            type="button"
            aria-label="Dismiss announcement"
            @click=${(event: Event) => this.dismissAnnouncement(event, announcement)}
          >
            Close
          </button>`
        : nothing}
    `;

    return html`<div
      data-announcement-id=${announcement.id || ''}
      style=${style}
      role=${announcement.linkUrl ? 'link' : 'banner'}
      tabindex=${announcement.linkUrl ? '0' : '-1'}
      class=${`announcementBar ${clickableClass} ${multiClass} ${announcement.placement} ${announcement.alignment} ${announcement.behavior} effect-${announcement.backgroundEffect}`}
      @click=${(event: Event) => this.onAnnouncementBarClick(event, announcement)}
      @keydown=${(event: KeyboardEvent) => this.onAnnouncementBarKeydown(event, announcement)}
    >
      ${content}
    </div>`;
  }

  private renderCurrencySwitcher() {
    const converter = this.config.plugins?.currencyConverter;
    if (!converter?.active || !converter.showCurrencySwitcher) return nothing;
    if (converter.showSwitcherOnlyOutsideVietnam && converter.detectedCurrency === 'VND') {
      return nothing;
    }
    const currencies = getCurrencyOptions(converter);

    return html`<div
      class=${`currencySwitcher ${converter.switcherPosition}`}
      style=${`--cc-primary: ${converter.switcherPrimaryColor};`}
    >
      <span class="currencySwitcherIcon" aria-hidden="true">$</span>
      <select aria-label="Currency" .value=${this.selectedCurrency} @change=${this.handleCurrencyChange}>
        ${currencies.map(
          (currency) =>
            html`<option value=${currency} ?selected=${currency === this.selectedCurrency}>
              ${formatCurrencySwitcherLabel(currency, converter)}
            </option>`,
        )}
      </select>
    </div>`;
  }

  private renderShoppableVideos() {
    const carousels = this.getVisibleShoppableCarousels();
    if (!carousels.length) {
      return nothing;
    }

    return html`${carousels.map((shoppable) => html`<section
        class="shoppableSection"
        style=${this.getShoppableStyle(shoppable)}
      >
        <div class="shoppableInner">
          <h2 class="shoppableTitle">${shoppable.title}</h2>
          <div class="shoppableRail">
            ${shoppable.items.map(
              (item) => {
                const hasProduct = hasShoppableProduct(item);
                const cardTitle = getShoppableDisplayTitle(item);
                return html`<button
                  class="shoppableCard"
                  type="button"
                  @click=${() => this.openShoppableVideo(item, shoppable)}
                >
                  ${this.renderShoppableCardMedia(item)}
                  ${hasProduct || cardTitle
                    ? html`<span class=${`shoppableCardInfo ${hasProduct ? '' : 'textOnly'}`}>
                        ${hasProduct && item.productImage
                          ? html`<img src=${item.productImage} alt="" />`
                          : nothing}
                        <span>
                          ${cardTitle ? html`<strong>${cardTitle}</strong>` : nothing}
                          ${hasProduct
                            ? html`<span>${this.formatShoppablePrice(item.productPrice || 0)}</span>`
                            : nothing}
                        </span>
                      </span>`
                    : nothing}
                </button>`;
              },
            )}
          </div>
        </div>
      </section>`)}`;
  }

  private renderShoppableCardMedia(item: ShoppableVideoItemConfig) {
    const embedUrl = getExternalEmbedUrl(item.videoUrl, true);
    const displayTitle = getShoppableDisplayTitle(item);
    if (embedUrl) {
      return html`<span class="embedCrop">
        <iframe
          src=${embedUrl}
          title=${displayTitle || 'Shoppable video'}
          allow="autoplay; encrypted-media; picture-in-picture"
          loading="lazy"
          @load=${this.markShoppableMediaLoaded}
        ></iframe>
      </span>`;
    }

    if (isEmbeddableVideoUrl(item.videoUrl)) {
      return html`<video
        src=${item.videoUrl}
        autoplay
        muted
        loop
        playsinline
        preload="auto"
        .muted=${true}
        @canplay=${this.markShoppableMediaLoaded}
        @playing=${this.markShoppableMediaLoaded}
      ></video>`;
    }

    return html`<div class="videoFallback">${displayTitle}</div>`;
  }

  private applyShadowCustomCursor(config: CustomCursorConfig | undefined): void {
    if (!config?.active || !config.cursorUrl) {
      this.clearShadowCustomCursor();
      return;
    }

    this.style.setProperty('--dada-custom-cursor', formatCursorValue(config.cursorUrl));
    this.setAttribute('data-custom-cursor', '');
  }

  private clearShadowCustomCursor(): void {
    this.style.removeProperty('--dada-custom-cursor');
    this.removeAttribute('data-custom-cursor');
  }

  private renderShoppableVideoOverlay() {
    const item = this.activeVideo;
    if (!item) return nothing;
    const embedUrl = getExternalEmbedUrl(item.videoUrl, false);
    const nativeVideo = isEmbeddableVideoUrl(item.videoUrl);
    const hasProduct = hasShoppableProduct(item);
    const displayTitle = getShoppableDisplayTitle(item);
    const shoppable = this.activeVideoCarousel || this.getVisibleShoppableCarousels()[0];
    const cartBadge = this.cartItemCount && this.cartItemCount > 0 ? formatBadgeCount(this.cartItemCount) : '';

    return html`<div
      class="videoOverlay"
      style=${this.getShoppableStyle(shoppable)}
      tabindex="0"
      @wheel=${this.handleShoppableWheel}
      @touchstart=${this.handleShoppableTouchStart}
      @touchend=${this.handleShoppableTouchEnd}
      @keydown=${this.handleShoppableKeydown}
    >
      <div class="videoStage">
        ${embedUrl
          ? html`<iframe
              src=${embedUrl}
              title=${displayTitle || 'Shoppable video'}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowfullscreen
            ></iframe>`
          : nativeVideo
            ? html`<video
                src=${item.videoUrl}
                autoplay
                controlslist="nodownload"
                ?muted=${this.shoppableMuted}
                playsinline
                loop
                .muted=${this.shoppableMuted}
                @click=${this.tryPlayShoppableVideo}
                @loadedmetadata=${this.tryPlayShoppableVideo}
              ></video>`
            : item.productImage
              ? html`<img class="videoPoster" src=${item.productImage} alt="" />`
              : html`<div class="videoFallback">${displayTitle}</div>`}
        <button class="videoClose" type="button" aria-label="Close" @click=${this.closeShoppableVideo}>
          x
        </button>
        ${embedUrl || nativeVideo
          ? html`<button
              class="videoMute"
              type="button"
              aria-label=${this.shoppableMuted ? 'Turn audio on' : 'Turn audio off'}
              @click=${this.toggleShoppableMute}
            >
              ${this.renderShoppableAudioIcon()}
            </button>`
          : nothing}
        <button
          class="videoCart"
          type="button"
          aria-label="Open cart"
          @click=${this.openStorefrontCart}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="9" cy="20" r="1.5" />
            <circle cx="18" cy="20" r="1.5" />
            <path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 1.9-1.4L21 8H6" />
          </svg>
          ${cartBadge ? html`<span class="videoCartBadge">${cartBadge}</span>` : nothing}
        </button>
        ${hasProduct
          ? this.renderShoppableProductBanner(item)
          : displayTitle
            ? html`<div class="videoTitleOnly">${displayTitle}</div>`
            : nothing}
      </div>
    </div>`;
  }

  private renderShoppableProductBanner(item: ShoppableVideoItemConfig) {
    return html`<div
      class=${`productBanner ${this.shoppableExpanded ? 'expanded' : ''}`}
      @click=${() => (this.shoppableExpanded = true)}
    >
      ${this.shoppableExpanded
        ? html`<button
            class="productBannerClose"
            type="button"
            aria-label="Collapse product info"
            @click=${this.collapseShoppableProductBanner}
          >
            x
          </button>`
        : nothing}
      ${item.productImage ? html`<img src=${item.productImage} alt="" />` : nothing}
      <div>
        <h3>${item.productTitle}</h3>
        <div class="productPrice">
          <span>${this.formatShoppablePrice(item.productPrice || 0)}</span>
          ${item.productCompareAtPrice && item.productCompareAtPrice > (item.productPrice || 0)
            ? html`<s>${this.formatShoppablePrice(item.productCompareAtPrice)}</s>`
            : nothing}
        </div>
        ${this.shoppableExpanded && item.productDescription
          ? html`<p>${item.productDescription}</p>`
          : nothing}
      </div>
      ${this.shoppableExpanded
        ? html`<div class="productActions">
            <a href=${item.productUrl} target="_self">More info</a>
            <button type="button" @click=${(event: Event) => this.addShoppableProductToCart(event, item)}>
              ${this.cartNotice || 'Add to cart'}
            </button>
          </div>`
        : html`<button class="shopNow" type="button">Shop Now</button>`}
    </div>`;
  }

  private renderShoppableAudioIcon() {
    return this.shoppableMuted
      ? html`<svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 9v6h4l5 4V5L8 9H4z" />
          <path d="m18 9 4 4m0-4-4 4" />
        </svg>`
      : html`<svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 9v6h4l5 4V5L8 9H4z" />
          <path d="M17 9.5a4 4 0 0 1 0 5" />
          <path d="M19.5 7a7.5 7.5 0 0 1 0 10" />
        </svg>`;
  }

  private getShoppableStyle(shoppable: ShoppableVideoCarouselConfig | undefined): string {
    if (!shoppable) return '';
    return [
      `--sv-card-radius: ${clampNumber(shoppable.cardCornerRadius, 20, 0, 48)}px`,
      `--sv-card-border-size: ${clampNumber(shoppable.cardBorderSize, 0, 0, 12)}px`,
      `--sv-card-border-color: ${colorOrFallback(shoppable.cardBorderColor, '#111827')}`,
      `--sv-card-placeholder: ${colorOrFallback(shoppable.cardPlaceholderColor, '#111827')}`,
      `--sv-product-primary: ${colorOrFallback(shoppable.productBarPrimaryColor, '#000000')}`,
      `--sv-product-secondary: ${colorOrFallback(shoppable.productBarSecondaryColor, '#4b5563')}`,
      `--sv-product-bg: ${colorOrFallback(shoppable.productBarBackgroundColor, '#ffffff')}`,
    ].join('; ');
  }

  private renderForm() {
    return html`
      <form @submit=${this.onSubmit}>
        ${this.config.form.requiredFields.includes('email')
          ? html`<input
              autocomplete="email"
              inputmode="email"
              type="email"
              .value=${this.email}
              placeholder=${this.config.copy.emailPlaceholder}
              @input=${(event: Event) => (this.email = (event.target as HTMLInputElement).value)}
            />`
          : nothing}
        ${this.config.form.requiredFields.includes('phone')
          ? html`<div class="phoneField">
              <span class="phonePrefix" aria-label="Vietnam country code">
                <span class="phoneFlag" aria-hidden="true">🇻🇳</span>
                +84
              </span>
              <input
                autocomplete="tel-national"
                inputmode="tel"
                type="tel"
                .value=${this.phone}
                placeholder=${this.config.copy.phonePlaceholder}
                @input=${(event: Event) => (this.phone = (event.target as HTMLInputElement).value)}
              />
            </div>`
          : nothing}
        ${this.config.form.consentRequired
          ? html`<label class="consent">
              <input
                type="checkbox"
                required
                .checked=${this.consentAccepted}
                @change=${(event: Event) =>
                  (this.consentAccepted = (event.target as HTMLInputElement).checked)}
              />
              <span>${this.config.copy.consentLabel}</span>
            </label>`
          : nothing}
        ${this.error ? html`<div class="error" role="alert">${this.error}</div>` : nothing}
        <button
          class="submit"
          type="submit"
          ?disabled=${this.submitting ||
          this.spinning ||
          (this.config.form.consentRequired && !this.consentAccepted)}
        >
          ${this.spinning ? this.config.copy.spinningLabel : this.config.copy.submitLabel}
        </button>
      </form>
    `;
  }

  private renderResult() {
    const coupon = this.result?.coupon.couponCode;
    return html`
      <div class="resultHint">
        ${coupon
          ? 'Tap the coupon to copy it.'
          : this.result?.coupon.message || this.result?.spin.message || 'Thanks for playing.'}
      </div>
      ${coupon
        ? html`<button class="coupon" type="button" @click=${this.copyCoupon}>${coupon}</button>
            <div class="copyStatus">${this.copied ? 'Copied' : 'Ready to use at checkout'}</div>`
        : nothing}
      ${this.error ? html`<div class="error" role="alert">${this.error}</div>` : nothing}
    `;
  }

  private async boot(): Promise<void> {
    if (this.preview && this.previewConfig) {
      this.fingerprint = await getBrowserFingerprint();
      this.config = mergePreviewCampaignConfig(this.previewConfig);
      this.selectedCurrency = resolveInitialCurrency(
        this.config.plugins?.currencyConverter,
        this.config.shopDomain,
      );
      this.launcherReady = true;
      this.loading = false;
      return;
    }

    const options = this.getOptions();
    if (!options.apiBaseUrl) {
      this.error = 'Widget API URL is missing.';
      this.loading = false;
      return;
    }

    this.api = new SpinWheelApiClient(options);

    try {
      this.fingerprint = await getBrowserFingerprint();
      this.config = await this.api.getCampaignConfig({
        ...options,
        fingerprint: this.fingerprint,
      });
      this.selectedCurrency = resolveInitialCurrency(
        this.config.plugins?.currencyConverter,
        this.config.shopDomain,
      );
      this.runPluginSafely('custom cursor', () => applyCustomCursor(this.config.plugins?.customCursor));
      this.runPluginSafely('custom cursor shadow', () =>
        this.applyShadowCustomCursor(this.config.plugins?.customCursor),
      );
      this.runPluginSafely('announcement countdown', () =>
        this.syncCountdownTimer(this.getActiveAnnouncementBar()),
      );
      this.runPluginSafely('announcement rotation', () => this.syncAnnouncementRotationTimer());
      this.runPluginSafely('currency converter', () =>
        this.syncCurrencyConverter(this.config.plugins?.currencyConverter),
      );
      if (!this.config.active) return;
      this.trigger.start({
        config: this.config.trigger,
        storageKey: `spin-wheel:${this.config.shopDomain}:${this.config.campaignId}`,
        preview: this.preview,
        onReady: () => {
          this.launcherReady = true;
        },
        onOpen: () => this.openModal(),
      });
    } catch (error) {
      this.error = error instanceof Error ? error.message : this.config.copy.unavailableMessage;
      this.config = { ...this.config, active: false };
    } finally {
      this.loading = false;
    }
  }

  private syncCountdownTimer(config: AnnouncementBarConfig | undefined): void {
    this.stopCountdownTimer();
    if (!config?.active || !config.countdownEnabled) return;
    if (isAnnouncementCountdownExpired(config)) return;

    this.countdownTimer = window.setInterval(() => this.requestUpdate(), 1000);
  }

  private syncAnnouncementRotationTimer(): void {
    this.stopAnnouncementRotationTimer();
    const settings = this.config.plugins?.announcementBarSettings;
    const bars = this.getVisibleAnnouncementBars();
    if (!settings?.allowMultiple || settings.displayMode !== 'alternate' || bars.length < 2) {
      this.activeAnnouncementIndex = 0;
      return;
    }

    const intervalMs = Math.max(2, settings.rotationSeconds || 6) * 1000;
    this.announcementRotationTimer = window.setInterval(() => {
      const visibleBars = this.getVisibleAnnouncementBars();
      if (visibleBars.length < 2) {
        this.activeAnnouncementIndex = 0;
        return;
      }
      this.activeAnnouncementIndex = (this.activeAnnouncementIndex + 1) % visibleBars.length;
    }, intervalMs);
  }

  private syncAnnouncementPageOffset(): void {
    const announcement = this.getActiveAnnouncementBar();
    const shouldOffset =
      !this.preview &&
      announcement?.active &&
      announcement.behavior === 'fixed' &&
      announcement.placement === 'top' &&
      !this.isAnnouncementDismissed(announcement) &&
      !isAnnouncementSchedulePending(announcement) &&
      !isAnnouncementCountdownExpired(announcement);

    if (!shouldOffset) {
      this.restoreAnnouncementPageOffset();
      return;
    }

    window.requestAnimationFrame(() => {
      if (!this.isConnected) return;

      const bars = Array.from(this.renderRoot.querySelectorAll<HTMLElement>('.announcementBar.top'));
      if (!bars.length) {
        this.restoreAnnouncementPageOffset();
        return;
      }

      if (!this.announcementOffsetApplied) {
        this.announcementOriginalBodyPaddingTop = document.body.style.paddingTop;
        this.announcementOffsetApplied = true;
      }

      const height = bars.reduce(
        (total, bar) => total + Math.max(50, Math.ceil(bar.getBoundingClientRect().height)),
        0,
      );
      const originalPadding = this.announcementOriginalBodyPaddingTop || '0px';
      const nextPadding = `calc(${originalPadding} + ${height}px)`;
      if (document.body.style.paddingTop !== nextPadding) {
        document.body.style.paddingTop = nextPadding;
      }
    });
  }

  private scheduleAnnouncementMountPosition(): void {
    if (this.announcementMountFrame) return;
    this.announcementMountFrame = window.requestAnimationFrame(() => {
      this.announcementMountFrame = undefined;
      this.runPluginSafely('announcement mount frame', () => this.syncAnnouncementMountPosition());
    });
  }

  private syncAnnouncementMountPosition(): void {
    if (!this.isConnected) return;
    if (this.preview || !document.body) return;
    if (this.hasVisibleShoppableVideos()) return;

    const announcement = this.getActiveAnnouncementBar();
    if (!announcement?.active || announcement.behavior !== 'scrollable') {
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
      return;
    }

    if (announcement.placement === 'top') {
      const firstElement = document.body.firstElementChild;
      if (firstElement !== this) {
        document.body.insertBefore(this, firstElement);
      }
      return;
    }

    if (document.body.lastElementChild !== this) {
      document.body.appendChild(this);
    }
  }

  private cancelPendingPlacementWork(): void {
    if (this.announcementMountFrame) {
      window.cancelAnimationFrame(this.announcementMountFrame);
      this.announcementMountFrame = undefined;
    }
    if (this.shoppablePlacementFrame) {
      window.cancelAnimationFrame(this.shoppablePlacementFrame);
      this.shoppablePlacementFrame = undefined;
    }
  }

  private syncAnnouncementHostState(): void {
    const announcement = this.getActiveAnnouncementBar();
    const isScrollable = Boolean(announcement?.active && announcement.behavior === 'scrollable');
    this.toggleAttribute('data-announcement-scrollable', isScrollable);
    this.toggleAttribute('data-announcement-top', isScrollable && announcement?.placement === 'top');
    this.toggleAttribute('data-announcement-bottom', isScrollable && announcement?.placement === 'bottom');
  }

  private restoreAnnouncementPageOffset(): void {
    if (!this.announcementOffsetApplied) return;
    document.body.style.paddingTop = this.announcementOriginalBodyPaddingTop || '';
    this.announcementOriginalBodyPaddingTop = undefined;
    this.announcementOffsetApplied = false;
  }

  private syncCurrencyConverter(config: CurrencyConverterConfig | undefined): void {
    this.stopCurrencyConverter();
    if (!config?.active) {
      restoreConvertedPrices();
      return;
    }

    this.convertStorefrontPrices();
    this.currencyObserver = new MutationObserver(() => {
      window.clearTimeout(this.currencyScanTimer);
      this.currencyScanTimer = window.setTimeout(
        () => this.runPluginSafely('currency converter', () => this.convertStorefrontPrices()),
        80,
      );
    });
    if (!document.body) return;
    this.currencyObserver.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  private stopCurrencyConverter(): void {
    this.currencyObserver?.disconnect();
    this.currencyObserver = undefined;
    if (this.currencyScanTimer) window.clearTimeout(this.currencyScanTimer);
    this.currencyScanTimer = undefined;
  }

  private convertStorefrontPrices(): void {
    const converter = this.config.plugins?.currencyConverter;
    if (!converter?.active) return;
    try {
      convertStorefrontPrices(converter, this.selectedCurrency, this);
    } catch (error) {
      console.warn('[Dada Plugins] Currency conversion skipped.', error);
    }
  }

  private stopCountdownTimer(): void {
    if (this.countdownTimer) window.clearInterval(this.countdownTimer);
    this.countdownTimer = undefined;
  }

  private stopAnnouncementRotationTimer(): void {
    if (this.announcementRotationTimer) window.clearInterval(this.announcementRotationTimer);
    this.announcementRotationTimer = undefined;
  }

  private handleCurrencyChange = (event: Event): void => {
    const select = event.target as HTMLSelectElement;
    this.selectedCurrency = select.value as CurrencyCode;
    persistSelectedCurrency(this.config.shopDomain, this.selectedCurrency);
    this.convertStorefrontPrices();
    select.blur();
  };

  private formatShoppablePrice(value: number): string {
    const converter = this.config.plugins?.currencyConverter;
    if (
      converter?.active &&
      this.selectedCurrency !== 'VND' &&
      converter.rates[this.selectedCurrency]
    ) {
      return formatStorefrontCurrency(
        value * converter.rates[this.selectedCurrency],
        this.selectedCurrency,
      );
    }

    return formatVndPrice(value);
  }

  private async onSubmit(event: Event): Promise<void> {
    event.preventDefault();
    if (!this.api || this.submitting || this.spinning) return;

    this.error = '';
    const emailRequired = this.config.form.requiredFields.includes('email');
    const phoneRequired = this.config.form.requiredFields.includes('phone');

    if (emailRequired) {
      const emailCheck = validateEmail(this.email);
      if (!emailCheck.valid) {
        this.error = emailCheck.message || 'Email is invalid.';
        return;
      }
    }

    if (phoneRequired) {
      const phoneCheck = validateVietnamPhone(this.phone);
      if (!phoneCheck.valid) {
        this.error = phoneCheck.message || 'Phone is invalid.';
        return;
      }
    }

    if (this.config.form.consentRequired && !this.consentAccepted) {
      this.error = 'Please confirm consent before spinning.';
      return;
    }

    this.submitting = true;
    this.spinning = true;

    try {
      const lead = await this.api.createLead({
        campaignId: this.config.campaignId,
        shopDomain: this.config.shopDomain,
        email: this.email.trim().toLowerCase(),
        phone: normalizeVietnamPhone(this.phone),
        consentAccepted: this.consentAccepted,
        sourceUrl: window.location.href,
        userAgent: navigator.userAgent,
        locale: navigator.language,
        fingerprint: this.fingerprint,
      });
      this.leadId = lead.leadId;

      const spin = await this.api.spinWheel({
        campaignId: this.config.campaignId,
        shopDomain: this.config.shopDomain,
        leadId: this.leadId,
        fingerprint: this.fingerprint,
      });

      const target = this.renderer?.getTargetRotation(
        spin.resultSegmentId,
        this.config.segments,
        this.rotationDeg,
        spin.stopAngleDeg,
      );
      if (target !== undefined && this.renderer) {
        const from = this.rotationDeg;
        await this.renderer.animateTo(from, target, spin.durationMs || 4200, (rotationDeg) => {
          this.rotationDeg = rotationDeg;
          this.drawWheel();
        });
      }

      const segment =
        this.config.segments.find((item) => item.id === spin.resultSegmentId) ||
        this.config.segments[Number(spin.segmentIndex) || 0] ||
        this.config.segments[0];

      const coupon = spin.couponRequired
        ? await this.api.assignCoupon({
            campaignId: this.config.campaignId,
            shopDomain: this.config.shopDomain,
            leadId: this.leadId,
            spinId: spin.spinId,
            resultSegmentId: spin.resultSegmentId,
            rewardId: spin.rewardId,
          })
        : { message: spin.message || spin.resultLabel || spin.label };

      this.copied = false;
      this.result = { segment, spin, coupon };
    } catch (error) {
      this.error =
        error instanceof SpinWheelApiError ? error.message : 'Unable to complete the spin.';
    } finally {
      this.submitting = false;
      this.spinning = false;
    }
  }

  private openModal(): void {
    if (this.loading || !this.config.active) return;
    if (!this.open) void this.trackPopupOpen();
    this.launcherReady = true;
    this.open = true;
    this.dispatchEvent(new CustomEvent('spin-wheel:open', { bubbles: false, composed: false }));
  }

  private async trackPopupOpen(): Promise<void> {
    try {
      await this.api?.trackEvent({
        campaignId: this.config.campaignId,
        shopDomain: this.config.shopDomain,
        eventName: 'popup_open',
        fingerprint: this.fingerprint,
      });
    } catch {
      // Analytics must never block the widget experience.
    }
  }

  private async trackAnnouncementViewOnce(): Promise<void> {
    const announcement = this.getActiveAnnouncementBar();
    if (this.announcementViewTracked || !this.api || !announcement?.active) return;
    if (
      this.isAnnouncementDismissed(announcement) ||
      isAnnouncementSchedulePending(announcement) ||
      isAnnouncementCountdownExpired(announcement)
    ) {
      return;
    }

    this.announcementViewTracked = true;
    try {
      await this.api.trackAnnouncementEvent({
        shopDomain: this.config.shopDomain,
        announcementBarId: announcement.id,
        eventName: 'view',
      });
    } catch {
      // Announcement analytics must never block the widget experience.
    }
  }

  private trackAnnouncementClick = (announcement = this.getActiveAnnouncementBar()): void => {
    if (this.preview || !this.api || !announcement?.active) return;

    void this.api.trackAnnouncementEvent({
      shopDomain: this.config.shopDomain,
      announcementBarId: announcement.id,
      eventName: 'click',
    });
  };

  private getActiveAnnouncementBar(): AnnouncementBarConfig | undefined {
    return this.getRenderableAnnouncementBars()[0] || this.config.plugins?.announcementBar;
  }

  private getRenderableAnnouncementBars(): AnnouncementBarConfig[] {
    const settings = this.config.plugins?.announcementBarSettings;
    const visibleBars = this.getVisibleAnnouncementBars();
    if (!settings?.allowMultiple) return visibleBars.slice(0, 1);
    if (settings.displayMode !== 'alternate') return visibleBars;
    if (!visibleBars.length) return [];
    const index = this.activeAnnouncementIndex % visibleBars.length;
    return [visibleBars[index]];
  }

  private getVisibleAnnouncementBars(): AnnouncementBarConfig[] {
    const bars = this.config.plugins?.announcementBars?.length
      ? this.config.plugins.announcementBars
      : this.config.plugins?.announcementBar
        ? [this.config.plugins.announcementBar]
        : [];

    return bars.filter(
      (bar) =>
        bar.active &&
        !this.isAnnouncementDismissed(bar) &&
        !isAnnouncementSchedulePending(bar) &&
        !isAnnouncementCountdownExpired(bar),
    );
  }

  private onAnnouncementBarClick = (event: Event, announcement: AnnouncementBarConfig): void => {
    if (!announcement?.active) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('.announcementDismiss')) return;
    this.trackAnnouncementClick(announcement);
    if (!announcement.linkUrl) return;
    if (announcement.openInNewWindow !== false) {
      window.open(announcement.linkUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    window.location.assign(announcement.linkUrl);
  };

  private onAnnouncementBarKeydown = (event: KeyboardEvent, announcement: AnnouncementBarConfig): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onAnnouncementBarClick(event, announcement);
  };

  private dismissAnnouncement = (event: Event, announcement: AnnouncementBarConfig): void => {
    event.preventDefault();
    event.stopPropagation();
    if (!announcement?.active) return;
    if (announcement.id) this.dismissedAnnouncementIds.add(announcement.id);
    this.announcementDismissed = true;
    this.restoreAnnouncementPageOffset();
    if (announcement.dismissOnce) {
      const key = this.getAnnouncementDismissKey(announcement);
      try {
        localStorage.setItem(key, '1');
      } catch {
        // Local storage may be unavailable in strict browser modes.
      }
      try {
        setLocalCookie(key, '1', 365);
      } catch {
        // Cookies may be unavailable in strict browser modes.
      }
    }
    if (this.preview || !this.api) return;
    void this.api.trackAnnouncementEvent({
      shopDomain: this.config.shopDomain,
      announcementBarId: announcement.id,
      eventName: 'dismiss',
    });
  };

  private isAnnouncementDismissed(announcement: AnnouncementBarConfig): boolean {
    if (announcement.id && this.dismissedAnnouncementIds.has(announcement.id)) return true;
    if (this.announcementDismissed && !this.config.plugins?.announcementBarSettings?.allowMultiple) {
      return true;
    }
    if (!announcement.dismissOnce) return false;
    try {
      const key = this.getAnnouncementDismissKey(announcement);
      return localStorage.getItem(key) === '1' || getLocalCookie(key) === '1';
    } catch {
      return getLocalCookie(this.getAnnouncementDismissKey(announcement)) === '1';
    }
  }

  private getAnnouncementDismissKey(announcement: AnnouncementBarConfig): string {
    const signature = encodeURIComponent(
      announcement.id || `${announcement.title}|${announcement.subtitle}`,
    ).slice(0, 120);
    return `dada:announcement:dismissed:${this.config.shopDomain || 'shop'}:${signature}`;
  }

  private closeModal(): void {
    this.open = false;
  }

  private getVisibleShoppableCarousels(): ShoppableVideoCarouselConfig[] {
    const shoppable = this.config.plugins?.shoppableVideos;
    if (!shoppable) return [];
    const carousels = shoppable.carousels?.length ? shoppable.carousels : [shoppable];
    return carousels.filter(
      (carousel) =>
        carousel.active &&
        carousel.items.length &&
        (this.preview || matchesShoppablePageUrl(carousel.pageUrl, window.location.href)),
    );
  }

  private hasVisibleShoppableVideos(): boolean {
    return !this.preview && this.getVisibleShoppableCarousels().length > 0;
  }

  private scheduleShoppablePlacement(): void {
    if (this.shoppablePlacementFrame) return;
    this.shoppablePlacementFrame = window.requestAnimationFrame(() => {
      this.shoppablePlacementFrame = undefined;
      this.runPluginSafely('shoppable placement frame', () => this.syncShoppablePlacement());
    });
  }

  private syncShoppablePlacement(): void {
    if (!this.isConnected) return;
    if (this.preview) return;
    const shoppable = this.getVisibleShoppableCarousels()[0];
    if (!shoppable) return;

    const placement = this.getCurrentShoppablePlacement();
    const target = resolveShoppablePlacementTarget(placement);
    if (
      !target ||
      target.element === this ||
      this.contains(target.element) ||
      isShoppablePlacementCurrent(this, target)
    ) {
      return;
    }

    if (target.mode === 'before') {
      target.element.parentElement?.insertBefore(this, target.element);
      return;
    }

    if (target.mode === 'prepend') {
      target.element.insertBefore(this, target.element.firstChild);
      return;
    }

    if (target.mode === 'append') {
      target.element.appendChild(this);
      return;
    }

    target.element.insertAdjacentElement('afterend', this);
  }

  private getCurrentShoppablePlacement(): ShoppablePlacementConfig {
    return this.getVisibleShoppableCarousels()[0]?.placement || { preset: 'after-hero', customSelector: '' };
  }

  private syncProductCountdown(): void {
    const item = this.getActiveProductCountdownItem();
    if (!item) {
      this.clearProductCountdownBadge();
      return;
    }

    const title = resolveProductTitleElement();
    if (!title) {
      this.clearProductCountdownBadge(false);
      return;
    }

    if (!this.productCountdownBadge) {
      this.productCountdownBadge = document.createElement('span');
      this.productCountdownBadge.className = 'dada-product-countdown';
      this.productCountdownBadge.setAttribute('aria-live', 'polite');
    }

    this.applyProductCountdownBadgeStyle(item);
    this.placeProductCountdownBadge(title, item.position);
    this.productCountdownBadge.textContent = formatProductCountdown(item.endsAt);

    if (!this.productCountdownTimer) {
      this.productCountdownTimer = window.setInterval(() => {
        const currentItem = this.getActiveProductCountdownItem();
        if (!currentItem || !this.productCountdownBadge) {
          this.clearProductCountdownBadge();
          return;
        }
        this.applyProductCountdownBadgeStyle(currentItem);
        this.productCountdownBadge.textContent = formatProductCountdown(currentItem.endsAt);
      }, 1000);
    }
  }

  private applyProductCountdownBadgeStyle(item: ProductCountdownItemConfig): void {
    if (!this.productCountdownBadge) return;

    Object.assign(this.productCountdownBadge.style, {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      width: '96px',
      maxWidth: '100%',
      margin: '0',
      padding: '5px 10px',
      borderRadius: `${clampNumber(item.borderRadius, 50, 0, 999)}px`,
      border: `${clampNumber(item.borderWidth, 0, 0, 8)}px solid ${colorOrFallback(item.borderColor, '#111827')}`,
      background: colorOrFallback(item.backgroundColor, '#111827'),
      color: colorOrFallback(item.textColor, '#ffffff'),
      font: '600 12px/1.1 system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    });
  }

  private placeProductCountdownBadge(title: HTMLElement, position: ProductCountdownItemConfig['position']): void {
    if (!this.productCountdownBadge) return;
    const safePosition = position || 'right';
    const wrapper = this.ensureProductCountdownTitleWrapper(title);
    const content = wrapper.querySelector<HTMLElement>('.dada-product-countdown-title-content');

    Object.assign(wrapper.style, {
      display: 'inline-flex',
      flexDirection: safePosition === 'above' || safePosition === 'below' ? 'column' : 'row',
      alignItems: safePosition === 'above' || safePosition === 'below' ? 'flex-start' : 'center',
      gap: safePosition === 'above' || safePosition === 'below' ? '8px' : '10px',
      maxWidth: '100%',
      verticalAlign: 'middle',
    });

    if (safePosition === 'left') {
      if (this.productCountdownBadge.parentElement !== wrapper || wrapper.firstChild !== this.productCountdownBadge) {
        wrapper.insertBefore(this.productCountdownBadge, content || wrapper.firstChild);
      }
      return;
    }

    if (safePosition === 'above') {
      if (this.productCountdownBadge.parentElement !== wrapper || wrapper.firstChild !== this.productCountdownBadge) {
        wrapper.insertBefore(this.productCountdownBadge, content || wrapper.firstChild);
      }
      return;
    }

    if (this.productCountdownBadge.parentElement !== wrapper || wrapper.lastChild !== this.productCountdownBadge) {
      wrapper.appendChild(this.productCountdownBadge);
    }
  }

  private ensureProductCountdownTitleWrapper(title: HTMLElement): HTMLElement {
    const existing = title.querySelector<HTMLElement>(':scope > .dada-product-countdown-title-wrap');
    if (existing) return existing;

    const wrapper = document.createElement('span');
    wrapper.className = 'dada-product-countdown-title-wrap';
    const content = document.createElement('span');
    content.className = 'dada-product-countdown-title-content';
    Object.assign(content.style, {
      display: 'inline',
      minWidth: '0',
    });

    const nodes = Array.from(title.childNodes).filter((node) => node !== this.productCountdownBadge);
    for (const node of nodes) {
      content.appendChild(node);
    }

    wrapper.appendChild(content);
    title.appendChild(wrapper);
    return wrapper;
  }

  private restoreProductCountdownTitleWrapper(): void {
    const wrapper = document.querySelector<HTMLElement>('.dada-product-countdown-title-wrap');
    if (!wrapper?.parentElement) return;
    const title = wrapper.parentElement;
    const content = wrapper.querySelector<HTMLElement>('.dada-product-countdown-title-content');
    if (!content) {
      wrapper.remove();
      return;
    }

    const nodes = Array.from(content.childNodes);
    for (const node of nodes) {
      title.insertBefore(node, wrapper);
    }
    wrapper.remove();
  }

  private clearProductCountdownBadge(clearTimer = true): void {
    this.productCountdownBadge?.remove();
    this.productCountdownBadge = undefined;
    this.restoreProductCountdownTitleWrapper();
    if (clearTimer && this.productCountdownTimer) {
      window.clearInterval(this.productCountdownTimer);
      this.productCountdownTimer = undefined;
    }
  }

  private getActiveProductCountdownItem(): ProductCountdownItemConfig | undefined {
    const items = this.config.plugins?.productCountdown?.items || [];
    const now = Date.now();
    return items.find((item) => {
      if (!item.active || !matchesProductCountdownUrl(item.productUrl, window.location.href)) return false;
      const startsAt = item.startsAt ? new Date(item.startsAt).getTime() : Number.NEGATIVE_INFINITY;
      const endsAt = item.endsAt ? new Date(item.endsAt).getTime() : Number.NaN;
      const validStart = item.startsAt ? Number.isFinite(startsAt) : true;
      return validStart && Number.isFinite(endsAt) && startsAt <= now && endsAt > now;
    });
  }

  private openShoppableVideo(item: ShoppableVideoItemConfig, carousel: ShoppableVideoCarouselConfig): void {
    this.activeVideo = item;
    this.activeVideoCarousel = carousel;
    this.shoppableExpanded = false;
    this.cartNotice = '';
    this.shoppableMuted = false;
    void this.refreshStorefrontCart(null, { refreshFragments: false, refreshTheme: false });
  }

  private closeShoppableVideo = (): void => {
    this.activeVideo = undefined;
    this.activeVideoCarousel = undefined;
    this.shoppableExpanded = false;
    this.cartNotice = '';
    this.shoppableTouchStartY = undefined;
  };

  private handleShoppableWheel = (event: WheelEvent): void => {
    if (!this.activeVideo) return;
    if (Math.abs(event.deltaY) < 36 || Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;
    event.preventDefault();
    event.stopPropagation();
    this.navigateShoppableVideo(event.deltaY > 0 ? 1 : -1);
  };

  private handleShoppableTouchStart = (event: TouchEvent): void => {
    this.shoppableTouchStartY = event.changedTouches[0]?.clientY;
  };

  private handleShoppableTouchEnd = (event: TouchEvent): void => {
    if (this.shoppableTouchStartY === undefined) return;
    const touchEndY = event.changedTouches[0]?.clientY;
    if (touchEndY === undefined) return;
    const deltaY = this.shoppableTouchStartY - touchEndY;
    this.shoppableTouchStartY = undefined;
    if (Math.abs(deltaY) < 54) return;
    event.preventDefault();
    event.stopPropagation();
    this.navigateShoppableVideo(deltaY > 0 ? 1 : -1);
  };

  private handleShoppableKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    this.navigateShoppableVideo(event.key === 'ArrowDown' ? 1 : -1);
  };

  private navigateShoppableVideo(direction: 1 | -1): void {
    const items = this.activeVideoCarousel?.items || [];
    if (!this.activeVideo || items.length < 2) return;

    const now = Date.now();
    if (now - this.shoppableLastNavigateAt < 520) return;
    this.shoppableLastNavigateAt = now;

    const currentIndex = Math.max(
      0,
      items.findIndex((item) => item.id === this.activeVideo?.id),
    );
    const nextIndex = (currentIndex + direction + items.length) % items.length;
    this.activeVideo = items[nextIndex];
    this.shoppableExpanded = false;
    this.cartNotice = '';
  }

  private playActiveShoppableVideo(): void {
    const video = this.renderRoot.querySelector<HTMLVideoElement>('.videoStage video');
    void video?.play().catch(() => {
      // Browser autoplay policy can still require an explicit tap.
    });
  }

  private toggleShoppableMute = (): void => {
    this.shoppableMuted = !this.shoppableMuted;
    const video = this.renderRoot.querySelector<HTMLVideoElement>('.videoStage video');
    if (video) {
      video.muted = this.shoppableMuted;
      if (!this.shoppableMuted) {
        void video.play().catch(() => {
          // Browser autoplay policy can still require an explicit tap.
        });
      }
    }
    this.updateEmbeddedPlayerAudio();
  };

  private collapseShoppableProductBanner = (event: Event): void => {
    event.stopPropagation();
    this.shoppableExpanded = false;
  };

  private updateEmbeddedPlayerAudio(): void {
    const iframe = this.renderRoot.querySelector<HTMLIFrameElement>('.videoStage iframe');
    if (!iframe?.contentWindow) return;
    const muted = this.shoppableMuted;
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func: muted ? 'mute' : 'unMute', args: [] }),
      '*',
    );
    iframe.contentWindow.postMessage(
      { method: 'setVolume', value: muted ? 0 : 1 },
      '*',
    );
    iframe.contentWindow.postMessage(
      { event: 'dada-plugins-audio', muted },
      '*',
    );
  }

  private tryPlayShoppableVideo = (event: Event): void => {
    const video = event.currentTarget as HTMLVideoElement | null;
    void video?.play().catch(() => {
      // Browser autoplay policy can still require an explicit tap.
    });
  };

  private markShoppableMediaLoaded = (event: Event): void => {
    (event.currentTarget as HTMLElement | null)?.classList.add('loaded');
  };

  private async addShoppableProductToCart(
    event: Event,
    item: ShoppableVideoItemConfig,
  ): Promise<void> {
    event.stopPropagation();
    if (!item.variantId) {
      window.location.href = item.productUrl;
      return;
    }

    this.cartNotice = 'Adding...';
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        cache: 'no-store',
        body: JSON.stringify({ id: item.variantId, quantity: 1 }),
      });
      if (!response.ok) throw new Error('Unable to add product.');
      const addResponse = await response.json().catch(() => null);
      await delay(180);
      await this.refreshStorefrontCart(addResponse, {
        quantityAdded: 1,
        refreshFragments: true,
        refreshTheme: true,
        notify: true,
        suppressAlerts: true,
      });
      this.cartNotice = 'Added';
    } catch {
      window.location.href = item.productUrl;
    }
  }

  private async refreshStorefrontCart(
    addResponse: unknown,
    options: {
      notify?: boolean;
      quantityAdded?: number;
      refreshFragments?: boolean;
      refreshTheme?: boolean;
      suppressAlerts?: boolean;
    } = {},
  ): Promise<void> {
    const {
      notify = true,
      quantityAdded = 0,
      refreshFragments = true,
      refreshTheme = false,
      suppressAlerts = false,
    } = options;
    const cart = await fetchCartJson();
    const itemCount =
      resolveCartItemCount(cart) ??
      (quantityAdded > 0 ? (this.cartItemCount || 0) + quantityAdded : undefined);

    if (itemCount !== undefined) {
      this.cartItemCount = itemCount;
      updateCartCountBadges(itemCount);
    }

    const detail = { cart, addResponse };
    if (refreshFragments) await refreshCartDomFragments();
    const updateTheme = (): void => {
      if (refreshTheme) callThemeCartHooks(cart);
      if (notify) notifyCartUpdated(detail);
    };
    if (suppressAlerts) {
      await withSuppressedSystemDialogs(updateTheme);
      if (itemCount !== undefined) updateCartCountBadges(itemCount);
      return;
    }
    updateTheme();
    if (itemCount !== undefined) updateCartCountBadges(itemCount);
  }

  private openStorefrontCart = (event?: Event): void => {
    event?.stopPropagation();
    const selectors = [
      '[data-cart-toggle]',
      '[data-drawer-trigger="cart"]',
      '[data-cart-drawer-trigger]',
      '.js-cart-toggle',
      '.cart-toggle',
      '.site-header__cart',
      '.header-cart',
      '.cart-link',
      '.mini-cart-trigger',
      'button[name="cart"]',
      'button[data-action*="cart" i]',
      '[aria-controls*="cart" i]',
      '[aria-label*="cart" i]',
      '[title*="cart" i]',
    ];

    const trigger = selectors
      .map((selector) => document.querySelector<HTMLElement>(selector))
      .filter((element): element is HTMLElement => Boolean(element))
      .filter((element) => {
        if (!(element instanceof HTMLAnchorElement)) return true;
        return !/\/cart(?:[?#].*)?$/i.test(element.getAttribute('href') || '');
      })
      .find(Boolean);
    if (trigger) {
      trigger.click();
      this.scheduleCartPageFallback();
      return;
    }

    window.dispatchEvent(new CustomEvent('cart:open'));
    window.dispatchEvent(new CustomEvent('haravan:cart:open'));
    window.dispatchEvent(new CustomEvent('theme:cart:open'));
    window.dispatchEvent(new CustomEvent('ajaxCart:open'));
    document.dispatchEvent(new CustomEvent('cart:open'));
    this.scheduleCartPageFallback();
  };

  private scheduleCartPageFallback(): void {
    window.setTimeout(() => {
      if (this.isCartDrawerOpen()) return;
      window.location.assign('/cart');
    }, 550);
  }

  private isCartDrawerOpen(): boolean {
    const bodyClass = document.body.className || '';
    if (/\b(cart|drawer|mini-cart)[-_]?(open|active|visible)\b/i.test(bodyClass)) return true;

    const openSelectors = [
      '[data-cart-drawer][aria-hidden="false"]',
      '[data-cart-drawer].open',
      '[data-cart-drawer].active',
      '[data-cart-drawer].is-open',
      '.cart-drawer.open',
      '.cart-drawer.active',
      '.cart-drawer.is-open',
      '.drawer-cart.open',
      '.drawer-cart.active',
      '.drawer-cart.is-open',
      '.mini-cart.open',
      '.mini-cart.active',
      '.mini-cart.is-open',
      '.ajaxcart.open',
      '.ajaxcart.active',
      '.ajaxcart.is-open',
      '[aria-modal="true"][class*="cart" i]',
    ];

    return openSelectors.some((selector) => {
      const element = document.querySelector<HTMLElement>(selector);
      if (!element) return false;
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        Number(style.opacity || '1') > 0 &&
        rect.width > 0 &&
        rect.height > 0
      );
    });
  }

  private async copyCoupon(): Promise<void> {
    const coupon = this.result?.coupon.couponCode;
    if (!coupon) return;

    try {
      await navigator.clipboard.writeText(coupon);
      this.copied = true;
    } catch {
      this.error = 'Unable to copy automatically. Select and copy the coupon code.';
    }
  }

  private onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) this.closeModal();
  }

  private drawWheel(): void {
    if (!this.renderer || !this.config.segments.length) return;
    this.renderer.draw({
      rotationDeg: this.rotationDeg,
      segments: this.config.segments,
      theme: this.config.theme,
    });
  }

  private applyTheme(): void {
    const style = this.style;
    style.setProperty('--sw-primary', this.config.theme.primaryColor);
    style.setProperty('--sw-accent', this.config.theme.accentColor);
    style.setProperty('--sw-text', this.config.theme.textColor);
    style.setProperty('--sw-muted', this.config.theme.mutedTextColor);
    style.setProperty('--sw-surface', this.config.theme.surfaceColor);
    style.setProperty('--sw-overlay', this.config.theme.overlayColor);
    style.setProperty('--sw-border', this.config.theme.borderColor);
    style.setProperty('--sw-button-text', this.config.theme.buttonTextColor);
    style.setProperty(
      '--sw-launcher-gradient',
      getLauncherGradient(this.config.theme.launcherColors),
    );
    style.setProperty('--sw-launcher-pointer', this.config.theme.launcherPointerColor);
    style.setProperty('--sw-launcher-divider', this.config.theme.launcherDividerColor);
    style.setProperty('--sw-launcher-text', this.config.theme.launcherTextColor);
  }

  private getOptions(): WidgetRuntimeOptions {
    return {
      apiBaseUrl: import.meta.env.VITE_SPIN_WHEEL_API_BASE_URL || defaultApiBaseUrl,
      shopDomain: getShopDomainFromLocation(),
      locale: navigator.language,
      preview: this.preview || getScriptDataset('preview') === 'true',
    };
  }
}

function getShopDomainFromLocation(): string {
  return window.location.hostname.toLowerCase().replace(/^www\./, '');
}

function getLauncherGradient(colors: string[]): string {
  const safeColors = colors.length === 3 ? colors : ['#0f766e', '#f59e0b', '#2563eb'];
  const segmentSize = 180 / safeColors.length;
  const stops = safeColors.flatMap((color, index) => {
    const start = index * segmentSize;
    const end = (index + 1) * segmentSize;
    return [`${color} ${start}deg ${end}deg`];
  });

  return `conic-gradient(from 0deg at 0 50%, ${stops.join(', ')}, transparent 180deg 360deg)`;
}

function getScriptDataset(key: string): string {
  const scripts = Array.from(document.scripts);
  const current = document.currentScript as HTMLScriptElement | null;
  const script =
    current ||
    scripts.find(
      (item) => item.src.includes('haravan-spin-wheel') || item.src.includes('widget.js'),
    );
  return script?.dataset[key] || '';
}

async function getBrowserFingerprint(): Promise<string> {
  const raw = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ].join('|');

  const bytes = new TextEncoder().encode(raw);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function formatAnnouncementCountdown(value: string): string {
  const remainingMs = Math.max(0, new Date(value).getTime() - Date.now());
  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function isAnnouncementCountdownExpired(config: AnnouncementBarConfig): boolean {
  if (!config.countdownEnabled || !config.countdownEndsAt) {
    return false;
  }

  return new Date(config.countdownEndsAt).getTime() <= Date.now();
}

function isAnnouncementSchedulePending(config: AnnouncementBarConfig): boolean {
  if (!config.countdownEnabled || !config.countdownStartsAt) return false;
  return new Date(config.countdownStartsAt).getTime() > Date.now();
}

function isAnnouncementCountdownStarted(config: AnnouncementBarConfig): boolean {
  if (!config.countdownEnabled || !config.countdownEndsAt) return false;
  if (!config.countdownStartsAt) return true;
  return new Date(config.countdownStartsAt).getTime() <= Date.now();
}

function getCurrencyOptions(config: CurrencyConverterConfig): CurrencyCode[] {
  const targets = config.targetCurrencies.filter((currency) =>
    supportedCurrencyCodes.includes(currency),
  );
  return ['VND', ...targets.filter((currency, index) => targets.indexOf(currency) === index)];
}

function resolveInitialCurrency(
  config: CurrencyConverterConfig | undefined,
  shopDomain = '',
): CurrencyCode {
  if (!config?.active) return 'VND';
  const options = getCurrencyOptions(config);
  const storedCurrency = config.showCurrencySwitcher
    ? readStoredCurrency(shopDomain, options)
    : undefined;
  if (storedCurrency) return storedCurrency;
  if (
    config.detectedCurrency &&
    options.includes(config.detectedCurrency)
  ) {
    return config.detectedCurrency;
  }
  return config.targetCurrencies[0] || options[0] || 'VND';
}

function getCurrencyStorageKey(shopDomain: string): string {
  return `${currencyStorageKeyPrefix}:${shopDomain || window.location.hostname || 'store'}`;
}

function readStoredCurrency(shopDomain: string, options: CurrencyCode[]): CurrencyCode | undefined {
  try {
    const storedCurrency = window.localStorage.getItem(getCurrencyStorageKey(shopDomain));
    return options.includes(storedCurrency as CurrencyCode)
      ? (storedCurrency as CurrencyCode)
      : undefined;
  } catch {
    return undefined;
  }
}

function persistSelectedCurrency(shopDomain: string, currency: CurrencyCode): void {
  try {
    window.localStorage.setItem(getCurrencyStorageKey(shopDomain), currency);
  } catch {
    // Some storefront privacy settings block storage; conversion still works for the current page.
  }
}

function formatCurrencySwitcherLabel(
  currency: CurrencyCode,
  config: CurrencyConverterConfig,
): string {
  const parts: string[] = [];
  if (config.showCurrencySymbol) parts.push(currencySymbol(currency));
  if (config.showCurrencyCode) parts.push(currency);
  return parts.length ? parts.join(' ') : currency;
}

function currencySymbol(currency: CurrencyCode): string {
  const symbols: Record<CurrencyCode, string> = {
    VND: '₫',
    USD: '$',
    EUR: '€',
    JPY: '¥',
    INR: '₹',
    CNY: '¥',
    IDR: 'Rp',
    THB: '฿',
    KRW: '₩',
    MYR: 'RM',
  };
  return symbols[currency];
}

function convertStorefrontPrices(
  config: CurrencyConverterConfig,
  selectedCurrency: CurrencyCode,
  hostElement: HTMLElement,
): void {
  const priceElements = getPriceElements(hostElement);
  priceElements.forEach((element) => {
    convertPriceTextNodes(element, config, selectedCurrency);
  });
}

function restoreConvertedPrices(): void {
  document
    .querySelectorAll<HTMLElement>(`[${currencyOriginalValueAttr}]`)
    .forEach((element) => {
      restorePriceTextNodes(element);
      element.removeAttribute(currencyOriginalValueAttr);
    });
}

function getPriceElements(hostElement: HTMLElement): HTMLElement[] {
  const selector = [
    '.money',
    '.Money',
    '.amount',
    '.Amount',
    '.price',
    '.product-price',
    '.product__price',
    '.price-item',
    '.price__regular',
    '.price__sale',
    '.regular-price',
    '.sale-price',
    '.current-price',
    '.current_price',
    '.final-price',
    '.final_price',
    '.original-price',
    '.original_price',
    '.compare-price',
    '.compare-at-price',
    '.price__compare',
    '.was-price',
    '.old-price',
    '.list-price',
    '.cart-total',
    '.cart__total',
    '.cart-subtotal',
    '.cart__subtotal',
    '.cart-summary',
    '.cart-drawer__total',
    '.ajaxcart__total',
    '.mini-cart__total',
    '.subtotal',
    '.total',
    '.summary',
    '.order-summary',
    '.checkout-total',
    '.checkout__total',
    '.totals__total',
    '.totals__subtotal',
    '.line-item__price',
    '.line-item-price',
    '.line_price',
    '.item-price',
    '.item__price',
    '.cart-item__price',
    '.cart-item-price',
    '.cart-item__totals',
    '.cart__price',
    '.cart-price',
    '.cart_item_price',
    '.cart_item__price',
    's',
    'del',
    '[class*="money"]',
    '[class*="Money"]',
    '[class*="amount"]',
    '[class*="Amount"]',
    '[class*="price"]',
    '[class*="Price"]',
    '[class*="regular"]',
    '[class*="Regular"]',
    '[class*="sale"]',
    '[class*="Sale"]',
    '[class*="original"]',
    '[class*="Original"]',
    '[class*="compare"]',
    '[class*="Compare"]',
    '[class*="total"]',
    '[class*="Total"]',
    '[class*="subtotal"]',
    '[class*="Subtotal"]',
    '[data-price]',
    '[data-cart-total]',
    '[data-subtotal]',
    '[data-total-price]',
    '[data-line-price]',
    '[data-cart-subtotal]',
  ].join(',');

  const elements = Array.from(document.querySelectorAll<HTMLElement>(selector)).filter(
    (element) =>
      !hostElement.contains(element) &&
      Boolean(element.textContent?.trim()),
  );

  if (isCartPage()) {
    const cartSelector = [
      'main',
      '[role="main"]',
      '#cart',
      '.cart',
      '.cart-page',
      '.cart-template',
      '.cart__items',
      '.cart-items',
      '.cart-item',
      '.line-item',
      '.ajaxcart',
      '.mini-cart',
      '.cart-drawer',
      '[class*="cart"]',
      '[class*="Cart"]',
    ].join(',');

    document.querySelectorAll<HTMLElement>(cartSelector).forEach((element) => {
      if (
        hostElement.contains(element) ||
        !element.textContent?.trim() ||
        !getVndPricePattern().test(element.textContent)
      ) {
        return;
      }
      elements.push(element);
    });
  }

  return Array.from(new Set(elements));
}

function isCartPage(): boolean {
  return /^\/cart(?:\/|$)/i.test(window.location.pathname);
}

function convertPriceTextNodes(
  element: HTMLElement,
  config: CurrencyConverterConfig,
  selectedCurrency: CurrencyCode,
): void {
  let converted = false;
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  textNodes.forEach((node) => {
    const originalText = originalCurrencyText.get(node) || node.nodeValue || '';
    const nextText =
      selectedCurrency === 'VND'
        ? originalText
        : convertPriceText(originalText, config, selectedCurrency);

    if (nextText !== originalText) converted = true;
    if (nextText !== node.nodeValue) {
      if (!originalCurrencyText.has(node)) originalCurrencyText.set(node, originalText);
      node.nodeValue = nextText;
    }
  });

  if (selectedCurrency === 'VND') {
    element.removeAttribute(currencyOriginalValueAttr);
    return;
  }

  if (converted) element.setAttribute(currencyOriginalValueAttr, 'true');
}

function restorePriceTextNodes(element: HTMLElement): void {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    const originalText = originalCurrencyText.get(node);
    if (originalText !== undefined) node.nodeValue = originalText;
  }
}

function convertPriceText(
  text: string,
  config: CurrencyConverterConfig,
  selectedCurrency: Exclude<CurrencyCode, 'VND'>,
): string {
  const rate = config.rates[selectedCurrency];
  if (!rate) return text;

  return text.replace(getVndPricePattern(), (token) => {
    const value = parseVndPriceToken(token);
    if (!value) return token;
    return formatStorefrontCurrency(value * rate, selectedCurrency);
  });
}

function getVndPricePattern(): RegExp {
  return /(?:₫|đ|vnd)\s*\d{1,3}(?:[.,]\d{3})+|(?:₫|đ|vnd)\s*\d{4,12}|\d{1,3}(?:[.,]\d{3})+\s*(?:₫|đ|vnd)|\d{4,12}\s*(?:₫|đ|vnd)/gi;
}

function parseVndPriceToken(token: string): number | null {
  const normalized = token.replace(/\s/g, '');
  const hasVndSignal = /₫|đ|vnd/i.test(normalized);
  if (!hasVndSignal) return null;

  const numeric = normalized.replace(/[^\d]/g, '');
  const value = Number(numeric);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function formatStorefrontCurrency(value: number, currency: CurrencyCode): string {
  return new Intl.NumberFormat(currencyLocale(currency), {
    style: 'currency',
    currency,
    maximumFractionDigits: zeroDecimalCurrency(currency) ? 0 : 2,
  }).format(value);
}

function currencyLocale(currency: CurrencyCode): string {
  const locales: Record<CurrencyCode, string> = {
    VND: 'vi-VN',
    USD: 'en-US',
    EUR: 'de-DE',
    JPY: 'ja-JP',
    INR: 'en-IN',
    CNY: 'zh-CN',
    IDR: 'id-ID',
    THB: 'th-TH',
    KRW: 'ko-KR',
    MYR: 'ms-MY',
  };
  return locales[currency];
}

function zeroDecimalCurrency(currency: CurrencyCode): boolean {
  return ['VND', 'JPY', 'IDR', 'KRW'].includes(currency);
}

function isKnownHaravanExternalScriptError(event: Event): boolean {
  const target = event.target as HTMLElement | null;
  const source =
    target instanceof HTMLScriptElement
      ? target.src
      : event instanceof ErrorEvent
        ? event.filename || event.message
        : '';

  return /\/phone_auth\/lib\.js(?:[?#].*)?$/i.test(source);
}

function formatVndPrice(value: number): string {
  if (!value) return '';
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value);
}

function updateCartCountBadges(itemCount: number): void {
  const selectors = [
    '[data-cart-count]',
    '[data-cart-items-count]',
    '[data-cart-item-count]',
    '[data-cart-total-items]',
    '[data-count]',
    '.cart-count',
    '.cartCount',
    '.cart-count-bubble',
    '.cart-item-count',
    '.cart-number',
    '.cart-quantity',
    '.cartQuantity',
    '.js-cart-count',
    '.js-cart-count-number',
    '.site-header__cart-count',
    '.site-header__cart .count',
    '.header-cart-count',
    '.header__cart-count',
    '.header-cart__count',
    '.header__cart .count',
    '.header-cart .count',
    '.top-cart .count',
    '.top-cart-count',
    '.cart-count-header',
    '.cartCountHeader',
    '.cart-number-header',
    '.cart_quantity',
    '.cart_qty',
    '.cartQty',
    '.count_item_pr',
    '.count_item',
    '.hd_cart_count',
    '.cart-icon-count',
    '.cart-icon__count',
    '.cart-total-count',
    '.cart-items-count',
    '.cart-link__count',
    '.cart-link .count',
    '.cart a .count',
    '.cart-icon .count',
    '.shopping-cart .count',
    '[class*="cart"][class*="count"]',
    '[id*="cart"][id*="count"]',
    '#CartCount',
    '[class*="cart-count"]',
  ];
  const badges = new Set<HTMLElement>();
  selectors.forEach((selector) => {
    document.querySelectorAll<HTMLElement>(selector).forEach((element) => badges.add(element));
  });

  badges.forEach((element) => {
    element.textContent = String(itemCount);
    element.setAttribute('data-cart-count', String(itemCount));
    element.setAttribute('data-count', String(itemCount));
    element.setAttribute('data-item-count', String(itemCount));
    element.setAttribute('data-cart-items-count', String(itemCount));
    element.setAttribute('aria-label', `${itemCount} items in cart`);
    element.classList.toggle('is-empty', itemCount <= 0);
    element.classList.toggle('hidden', itemCount <= 0);
    element.style.removeProperty('display');
  });
}

async function fetchCartJson(): Promise<unknown> {
  return fetch(`/cart.js?dada=${Date.now()}`, {
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
    cache: 'no-store',
  })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);
}

function resolveCartItemCount(cart: unknown): number | undefined {
  const payload = (cart || {}) as Record<string, unknown>;
  const direct = [
    payload.item_count,
    payload.itemCount,
    payload.items_count,
    payload.total_quantity,
    payload.quantity,
    payload.count,
  ];

  for (const value of direct) {
    const numberValue = Number(value);
    if (Number.isFinite(numberValue) && numberValue >= 0) return numberValue;
  }

  if (Array.isArray(payload.items)) {
    const count = payload.items.reduce((sum, item) => {
      const quantity = Number((item as Record<string, unknown>)?.quantity || 0);
      return sum + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
    }, 0);
    return count;
  }

  return undefined;
}

async function refreshCartDomFragments(): Promise<void> {
  const fragmentSelectors = [
    '#CartDrawer',
    '#cart-drawer',
    '[data-cart-drawer]',
    '[data-cart-container]',
    '[data-cart-items]',
    '[data-cart-line-items]',
    '[data-mini-cart]',
    '.cart-drawer',
    '.cart-drawer__items',
    '.cart-drawer__body',
    '.cart-drawer__content',
    '.mini-cart',
    '.mini-cart__items',
    '.mini-cart__content',
    '.ajaxcart',
    '.ajaxcart__inner',
    '.ajaxcart__products',
    '.cart-popup',
    '.cart-popup__items',
  ];
  const existing = fragmentSelectors
    .map((selector) => document.querySelector<HTMLElement>(selector))
    .filter((element): element is HTMLElement => Boolean(element));
  if (!existing.length) return;

  const views = ['/cart?view=drawer', '/cart?view=ajax', '/cart?view=mini', '/cart'];
  for (const view of views) {
    const htmlText = await fetch(`${view}${view.includes('?') ? '&' : '?'}dada=${Date.now()}`, {
      headers: {
        Accept: 'text/html',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
      cache: 'no-store',
    })
      .then((response) => (response.ok ? response.text() : ''))
      .catch(() => '');
    if (!htmlText) continue;

    const doc = new DOMParser().parseFromString(htmlText, 'text/html');
    let updated = false;
    existing.forEach((element) => {
      const selector = fragmentSelectors.find((candidate) => element.matches(candidate));
      if (!selector) return;
      const replacement = doc.querySelector<HTMLElement>(selector);
      if (!replacement) return;
      element.innerHTML = replacement.innerHTML;
      updated = true;
    });
    if (updated) return;
  }
}

function notifyCartUpdated(detail: { cart: unknown; addResponse: unknown }): void {
  const eventNames = [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'cart:update',
    'cart:build',
    'haravan:cart:updated',
    'theme:cart:updated',
    'theme:cart:reload',
    'ajaxCart:updated',
    'ajaxCart:refresh',
    'dada-plugins:cart-updated',
  ];
  eventNames.forEach((eventName) => {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  });

  const win = window as unknown as Window & {
    jQuery?: (target: Document | Window) => {
      trigger?: (eventName: string, payload?: unknown[]) => void;
    };
    $?: (target: Document | Window) => {
      trigger?: (eventName: string, payload?: unknown[]) => void;
    };
  };
  const jquery = win.jQuery || win.$;
  if (!jquery) return;
  eventNames.forEach((eventName) => {
    try {
      jquery(document).trigger?.(eventName, [detail.cart, detail.addResponse]);
    } catch {
      // jQuery is optional and differs between themes.
    }
  });
}

function callThemeCartHooks(cart: unknown): void {
  const win = window as unknown as Window &
    Record<string, unknown> & {
      ajaxCart?: Record<string, unknown>;
      theme?: Record<string, unknown>;
      Shopify?: Record<string, unknown>;
      Haravan?: Record<string, unknown>;
    };
  const hookOwners = [win.ajaxCart, win.theme, win.Shopify, win.Haravan, win];
  const methodNames = [
    'load',
    'refresh',
    'update',
    'render',
    'updateCart',
    'refreshCart',
    'buildCart',
    'getCart',
    'renderCart',
    'onCartUpdate',
    'onItemAdded',
  ];

  hookOwners.forEach((owner) => {
    if (!owner || typeof owner !== 'object') return;
    methodNames.forEach((methodName) => {
      const method = owner[methodName];
      if (typeof method !== 'function') return;
      try {
        method.call(owner, cart);
      } catch {
        // Theme hooks are optional and vary heavily across storefronts.
      }
    });
  });
}

async function withSuppressedSystemDialogs(action: () => void): Promise<void> {
  const originalAlert = window.alert;
  const originalConfirm = window.confirm;
  window.alert = () => undefined;
  window.confirm = () => true;
  try {
    action();
    await delay(500);
  } finally {
    window.alert = originalAlert;
    window.confirm = originalConfirm;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function formatBadgeCount(value: number): string {
  if (value > 99) return '99+';
  return String(value);
}

function hasShoppableProduct(item: ShoppableVideoItemConfig): boolean {
  return Boolean(item.productId && item.productTitle);
}

function getShoppableDisplayTitle(item: ShoppableVideoItemConfig): string {
  const explicitTitle = (item.title || '').trim();
  if (explicitTitle) return explicitTitle;
  if (hasShoppableProduct(item)) return (item.productTitle || '').trim();
  return '';
}

type ShoppablePlacementTarget = {
  element: HTMLElement;
  mode: 'before' | 'after' | 'prepend' | 'append';
};

function isShoppablePlacementCurrent(
  widget: HTMLElement,
  target: ShoppablePlacementTarget,
): boolean {
  if (target.mode === 'before') return target.element.previousElementSibling === widget;
  if (target.mode === 'after') return target.element.nextElementSibling === widget;
  if (target.mode === 'prepend') return target.element.firstElementChild === widget;
  if (target.mode === 'append') return target.element.lastElementChild === widget;
  return false;
}

function resolveShoppablePlacementTarget(
  placement: ShoppablePlacementConfig,
): ShoppablePlacementTarget | null {
  const preset = placement.preset || 'auto';

  if (preset === 'custom' && placement.customSelector.trim()) {
    const customTarget = safeQuerySelector(placement.customSelector);
    if (customTarget) return { element: customTarget, mode: placement.customMode === 'before' ? 'before' : 'after' };
  }

  const footer = document.querySelector<HTMLElement>('footer, .footer, #footer');
  const main = document.querySelector<HTMLElement>('main, #main, .main, .page-container') || document.body;

  if (preset === 'top') return { element: main, mode: 'prepend' };
  if (preset === 'before-footer' && footer) return { element: footer, mode: 'before' };

  const presetTarget = getPresetPlacementTarget(preset);
  if (presetTarget) return presetTarget;

  if (footer) return { element: footer, mode: 'before' };
  return { element: main, mode: 'append' };
}

function getPresetPlacementTarget(preset: ShoppablePlacementConfig['preset']): ShoppablePlacementTarget | null {
  const map: Partial<Record<ShoppablePlacementConfig['preset'], { selectors: string[]; mode: ShoppablePlacementTarget['mode'] }>> = {
    auto: {
      selectors: ['main, #main, .main, .page-container'],
      mode: 'append',
    },
    'after-hero': {
      selectors: [
        '[data-section-type*="slideshow" i]',
        '[class*="slideshow" i]',
        '[class*="hero" i]',
        '.home-slider',
        '.banner',
      ],
      mode: 'after',
    },
    'after-product-grid': {
      selectors: [
        '.product-grid',
        '.products-grid',
        '.collection-products',
        '.product-list',
        '[class*="collection" i] [class*="grid" i]',
      ],
      mode: 'after',
    },
    'after-product-images': {
      selectors: [
        '.product-gallery',
        '.product-images',
        '.product-media',
        '[class*="product" i] [class*="gallery" i]',
      ],
      mode: 'after',
    },
    'after-product-info': {
      selectors: [
        '.product-info',
        '.product-summary',
        '.product-detail-info',
        '[class*="product" i] [class*="summary" i]',
      ],
      mode: 'after',
    },
    'before-recommendations': {
      selectors: [
        '.product-recommendations',
        '.related-products',
        '[class*="recommend" i]',
        '[class*="related" i]',
      ],
      mode: 'before',
    },
  };

  const entry = map[preset];
  if (!entry) return null;
  for (const selector of entry.selectors) {
    const element = safeQuerySelector(selector);
    if (element) return { element, mode: entry.mode };
  }
  return null;
}

function safeQuerySelector(selector: string): HTMLElement | null {
  try {
    return document.querySelector<HTMLElement>(selector);
  } catch {
    return null;
  }
}

function matchesShoppablePageUrl(configuredUrl: string, currentUrl: string): boolean {
  const configured = normalizeComparablePageUrl(configuredUrl, currentUrl);
  const current = normalizeComparablePageUrl(currentUrl, currentUrl);
  return Boolean(configured && configured === current);
}

function matchesProductCountdownUrl(productUrl: string, currentUrl: string): boolean {
  const configured = normalizeProductPath(productUrl, currentUrl);
  const current = normalizeProductPath(currentUrl, currentUrl);
  return Boolean(configured && current && configured === current);
}

function normalizeProductPath(value: string, baseUrl: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    return normalizePathname(new URL(raw, baseUrl).pathname).toLowerCase();
  } catch {
    return normalizePathname(raw).toLowerCase();
  }
}

function resolveProductTitleElement(): HTMLElement | null {
  const selectors = [
    'h1[itemprop="name"]',
    '[itemprop="name"] h1',
    '.product-title',
    '.product-name',
    '.product-single__title',
    '.product-detail-name',
    '.product-info h1',
    '.product__title',
    'h1',
  ];

  for (const selector of selectors) {
    const element = safeQuerySelector(selector);
    if (element && element.textContent?.trim()) return element;
  }
  return null;
}

function formatProductCountdown(endsAt: string): string {
  const end = new Date(endsAt).getTime();
  const diff = Math.max(0, end - Date.now());
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${padTime(hours)}h ${padTime(minutes)}m`;
  return `${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}`;
}

function padTime(value: number): string {
  return String(Math.max(0, value)).padStart(2, '0');
}

function normalizeComparablePageUrl(value: string, baseUrl: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const url = new URL(raw, baseUrl);
    return `${url.origin}${normalizePathname(url.pathname)}${url.search}`.toLowerCase();
  } catch {
    return normalizePathname(raw).toLowerCase();
  }
}

function normalizePathname(pathname: string): string {
  const clean = String(pathname || '').split('#')[0].replace(/\/+$/, '');
  return clean || '/';
}

function isEmbeddableVideoUrl(url: string): boolean {
  return /\.(mp4|m4v|mov|webm|ogg)(?:\?|#|$)/i.test(url);
}

function getExternalEmbedUrl(url: string, muted = true): string {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '');
    const muteValue = muted ? '1' : '0';
    const mutedValue = muted ? '1' : '0';
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const videoId =
        parsed.pathname.startsWith('/shorts/')
          ? parsed.pathname.split('/')[2]
          : parsed.searchParams.get('v');
      return videoId
        ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${muteValue}&playsinline=1&controls=0&rel=0&loop=1&playlist=${videoId}&enablejsapi=1`
        : '';
    }
    if (host === 'youtu.be') {
      const videoId = parsed.pathname.replace('/', '');
      return videoId
        ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${muteValue}&playsinline=1&controls=0&rel=0&loop=1&playlist=${videoId}&enablejsapi=1`
        : '';
    }
    if (host === 'tiktok.com' || host === 'm.tiktok.com' || host === 'vm.tiktok.com') {
      const videoId = parsed.pathname.match(/\/video\/(\d+)/)?.[1];
      return videoId
        ? `https://www.tiktok.com/embed/v2/${videoId}?autoplay=1&muted=${mutedValue}&loop=1`
        : '';
    }
    if (host === 'instagram.com') {
      const [, type, shortcode] = parsed.pathname.match(/^\/(p|reel|tv)\/([^/]+)/) || [];
      return type && shortcode ? `https://www.instagram.com/${type}/${shortcode}/embed` : '';
    }
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const videoId =
        host === 'player.vimeo.com'
          ? parsed.pathname.match(/\/video\/(\d+)/)?.[1]
          : parsed.pathname.match(/^\/(\d+)/)?.[1];
      return videoId
        ? `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=${mutedValue}&loop=1&background=1`
        : '';
    }
    if (host === 'facebook.com' || host === 'm.facebook.com' || host === 'fb.watch') {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
        parsed.toString(),
      )}&autoplay=true&mute=${String(muted)}&show_text=false`;
    }
  } catch {
    return '';
  }
  return '';
}

function autoMount(): void {
  if (document.querySelector(elementName)) return;

  const element = document.createElement(elementName) as HaravanSpinWheelElement;
  const dataset = (document.currentScript as HTMLScriptElement | null)?.dataset;
  if (dataset?.preview === 'true') element.setAttribute('preview', '');
  (document.body || document.documentElement).appendChild(element);
}

function applyCustomCursor(config: CustomCursorConfig | undefined): void {
  if (!config?.active || !config.cursorUrl) {
    removeCustomCursor();
    return;
  }

  const style = document.getElementById(customCursorStyleId) || document.createElement('style');
  style.id = customCursorStyleId;
  style.textContent = `
    html, body, body * {
      cursor: ${formatCursorValue(config.cursorUrl)} !important;
    }
  `;
  if (!style.parentNode) document.head.appendChild(style);
}

function removeCustomCursor(): void {
  document.getElementById(customCursorStyleId)?.remove();
}

function formatCursorValue(url: string): string {
  const safeUrl = String(url).replace(/["\\\n\r]/g, '');
  return `url("${safeUrl}") 1 1, auto`;
}

function clampNumber(value: unknown, fallback: number, min: number, max: number): number {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return fallback;
  return Math.min(max, Math.max(min, numberValue));
}

function colorOrFallback(value: unknown, fallback: string): string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value.trim())
    ? value.trim()
    : fallback;
}

function getReadableTextColor(color: string): string {
  const safeColor = colorOrFallback(color, '#111827');
  const hex = safeColor.replace('#', '');
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
  return luminance > 0.62 ? '#172026' : '#ffffff';
}

function setLocalCookie(name: string, value: string, days: number): void {
  const maxAge = Math.max(1, Math.floor(days * 24 * 60 * 60));
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; samesite=lax`;
}

function getLocalCookie(name: string): string {
  const encodedName = `${encodeURIComponent(name)}=`;
  return (
    document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(encodedName))
      ?.slice(encodedName.length) || ''
  );
}

if (!window.__HARAVAN_SPIN_WHEEL_DISABLE_AUTOMOUNT__) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount, { once: true });
  } else {
    autoMount();
  }
}

declare global {
  interface Window {
    __HARAVAN_SPIN_WHEEL_DISABLE_AUTOMOUNT__?: boolean;
  }

  interface HTMLElementTagNameMap {
    [elementName]: HaravanSpinWheelElement;
  }
}

function mergePreviewCampaignConfig(config: Partial<CampaignConfig>): CampaignConfig {
  const merged = {
    ...fallbackCampaignConfig,
    ...config,
    segments: config.segments?.length ? config.segments : fallbackCampaignConfig.segments,
    theme: { ...fallbackCampaignConfig.theme, ...config.theme },
    copy: { ...fallbackCampaignConfig.copy, ...config.copy },
    trigger: {
      ...fallbackCampaignConfig.trigger,
      ...config.trigger,
      mode: 'manual' as const,
      delayMs: 0,
      showOncePerDays: 0,
    },
    form: { ...fallbackCampaignConfig.form, ...config.form },
    active: true,
  };

  return merged;
}
